<template>
  <div class="page-wrapper" ref="mainContainer">
    <div id="hero-marker" style="height: 1px;"></div>
    
    <!-- 3D Background Elements -->
    <div class="bg-3d-container">
      <div class="floating-orb orb-1"></div>
      <div class="floating-orb orb-2"></div>
      <div class="floating-orb orb-3"></div>
      <div class="floating-orb orb-4"></div>
      <div class="particle-field">
        <div class="particle" v-for="n in 20" :key="n" :style="{ 
          left: `${Math.random() * 100}%`, 
          top: `${Math.random() * 100}%`,
          animationDelay: `${Math.random() * 5}s`,
          animationDuration: `${10 + Math.random() * 10}s`
        }"></div>
      </div>
      <div class="geometric-shapes">
        <div class="shape shape-1"></div>
        <div class="shape shape-2"></div>
        <div class="shape shape-3"></div>
      </div>
    </div>
    
    <!-- 1. HERO SECTION WITH CLIP BACKGROUND & BEAM SEARCH -->
    <div class="hero-section" ref="heroSection">
      <!-- Background Clip Animation Columns -->
      <div class="bg-columns">
        <div class="bg-col" v-for="n in 5" :key="n" :style="{ animationDelay: `${n * 0.1}s` }"></div>
      </div>
      
      <!-- Gradient Orb Background -->
      <div class="hero-orb"></div>
    
      <!-- Hero Content -->
      <div class="hero-container">
        <div class="glass-card hero-card">
          <div class="card-content">
            
            <!-- Animated Text: Split by Letter -->
            <div class="text-wrapper">
              <transition name="text-fade" mode="out-in">
                <div :key="currentCardIndex" class="title-container">
                  <h1 class="staggered-text">
                    <span class="sr-only">{{ currentCard.title }}</span>
                    <span 
                      v-for="(char, index) in splitTitle" 
                      :key="index" 
                      class="char" 
                      :style="{ animationDelay: `${index * 0.03}s` }"
                      v-html="char === ' ' ? '&nbsp;' : char"
                    ></span>
                  </h1>
                  <p class="hero-subtitle reveal-blur">{{ currentCard.subtitle }}</p>
                </div>
              </transition>
            </div>
            <!-- BEAM SEARCH BAR (Premium Style) -->
            <div class="search-wrapper">
              <p class="search-label">{{ $t('home.searchLabel') }}</p>
              <div class="filter-tabs">
                <button
                  v-for="filter in filters"
                  :key="filter.key"
                  :class="['tab-btn', { active: selectedFilter === filter.key }]"
                  @click="setFilter(filter.key)"
                  @mouseenter="handleButtonHover"
                  @mouseleave="handleButtonLeave"
                >
                  <span class="btn-text">{{ $t(filter.labelKey) }}</span>
                  <span class="btn-ripple"></span>
                </button>
              </div>
              <div class="beam-container">
                <div class="beam-border"></div> <!-- The spinning glowing border -->
                <div class="search-inner">
                  <i class="el-icon-search search-icon"></i>
                  <input 
                    type="text" 
                    v-model="searchText" 
                    :placeholder="$t('app.searchPlaceholder')"
                    @keyup.enter="handleSearch"
                  />
                  <button class="go-btn" @click="handleSearch" @mouseenter="handleButtonHover" @mouseleave="handleButtonLeave">
                    <i class="el-icon-right"></i>
                    <span class="btn-pulse"></span>
                  </button>
                </div>
              </div>
            </div>
            <!-- Navigation Arrows -->
            <div class="hero-nav">
              <button 
                v-for="nav in navButtons" 
                :key="nav.direction"
                :class="['nav-btn', nav.direction]" 
                @click="nav.action" 
                @mouseenter="handleButtonHover" 
                @mouseleave="handleButtonLeave"
              >
                <i :class="nav.icon"></i>
                <span class="btn-glow"></span>
              </button>
            </div>
          </div>
          <!-- Dynamic Image Side -->
          <div class="card-visual">
            <transition name="bg-fade" mode="out-in">
              <div 
                :key="currentCardIndex" 
                class="visual-bg" 
                :style="{ backgroundImage: `url(${currentCard.image})` }"
              ></div>
            </transition>
            <div class="visual-overlay"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- 2. CATEGORY MARQUEE (What are you looking for?) -->
    <div class="category-marquee-section">
      <div class="category-header">
        <h2 class="category-title">{{ $t('home.searchLabel') }}</h2>
        <button class="see-more-btn" @click="ToHouseReviewScore">
          <span>{{ $t('home.seeMore') }}</span>
        </button>
      </div>
      <div class="category-marquee-mask">
        <div class="category-marquee-content">
          <div class="category-marquee-group">
            <div 
              v-for="(category, index) in categories" 
              :key="category.id"
              class="category-item"
              :style="{ animationDelay: `${index * 0.1}s` }"
              @click="handleCategoryClick(category)"
            >
              <i :class="category.icon"></i>
              <span class="category-label">{{ $t(category.labelKey) }}</span>
            </div>
          </div>
          <!-- Duplicate for infinite loop -->
          <div class="category-marquee-group" aria-hidden="true">
            <div 
              v-for="(category, index) in categories" 
              :key="category.id + '_dup'"
              class="category-item"
              :style="{ animationDelay: `${index * 0.1}s` }"
            >
              <i :class="category.icon"></i>
              <span class="category-label">{{ $t(category.labelKey) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 3. LOGO MARQUEE (Infinite Loop) -->
    <div class="marquee-section">
      <p class="marquee-label animate-on-scroll">{{ $t('home.trustedBy') }}</p>
      <div class="marquee-mask">
        <div class="marquee-content">
          <div class="marquee-group">
            <span 
              v-for="(uni, index) in universities" 
              :key="uni" 
              class="uni-logo"
              :style="{ animationDelay: `${index * 0.1}s` }"
            >{{ uni }}</span>
          </div>
          <!-- Duplicate for infinite loop -->
          <div class="marquee-group" aria-hidden="true">
            <span 
              v-for="(uni, index) in universities" 
              :key="uni + '_dup'" 
              class="uni-logo"
              :style="{ animationDelay: `${index * 0.1}s` }"
            >{{ uni }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 4. FEATURE GRID (Big Square Layout) -->
    <div class="content-container animate-on-scroll">
      <div class="feature-big-square">
        <div class="grid-layout">
        <div class="left-sticky animate-on-scroll">
          <div class="sticky-content">
            <h2>{{ $t('home.perfectHomeLine1') }}<br/>{{ $t('home.perfectHomeLine2') }}</h2>
            <p>{{ $t('home.realReviews') }}</p>
            <div class="svg-decoration">
              <img src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?ixlib=rb-1.2.1&auto=format&fit=crop&w=1920&q=80" alt="Students" class="hero-image">
            </div>
          </div>
        </div>

        <div class="feature-cards-container">
          <div class="right-grid" ref="rightGrid">
            <!-- Animated Connection Lines -->
            <!-- Dynamic paths that adjust to card positions -->
            <svg 
              ref="svgConnector"
              class="feature-connections" 
              viewBox="0 0 100 100" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg" 
              preserveAspectRatio="none"
            >
              <defs>
                <linearGradient id="railGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" style="stop-color:#3bc2ff; stop-opacity:0" />
                  <stop offset="20%" style="stop-color:#3bc2ff; stop-opacity:1" />
                  <stop offset="100%" style="stop-color:#3b82f6; stop-opacity:0" />
                </linearGradient>
              </defs>

              <!-- PATH 1: Top Row Rail -->
              <!-- Dynamically calculated path from left to top row center -->
              <path 
                :d="topRowPath" 
                stroke="url(#railGradient)" 
                stroke-width="1.5" 
                stroke-linecap="round"
                vector-effect="non-scaling-stroke"
                class="connection-line line-1"
              />

              <!-- PATH 2: Bottom Row Rail -->
              <!-- Dynamically calculated path from left to bottom row center -->
              <path 
                :d="bottomRowPath" 
                stroke="url(#railGradient)" 
                stroke-width="1.5" 
                stroke-linecap="round" 
                vector-effect="non-scaling-stroke"
                class="connection-line line-2"
              />
              
              <!-- Vertical Connector (Optional: joins the two rails) -->
              <path 
                :d="verticalPath" 
                stroke="#3bc2ff" 
                stroke-width="1.5" 
                stroke-linecap="round"
                class="connection-line line-3"
              />
            </svg>

            <div 
              v-for="(card, index) in featureCards"
              :key="card.ref"
              :ref="card.ref"
              class="feature-card-wrapper animate-on-scroll" 
              :style="{ transitionDelay: `${(index + 1) * 0.1}s` }" 
              :data-card="index + 1"
            >
              <div class="feature-card card-light-blue">
                <div class="card-beam-border"></div>
                <div class="card-shine"></div>
                <div class="card-content-inner">
                  <div :class="['icon-box', card.iconColor]">
                    <i :class="card.icon"></i>
                  </div>
                  <h3>{{ $t(card.titleKey) }}</h3>
                  <p>{{ $t(card.textKey) }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>

    <!-- 5. PROPERTY MANAGER CARD (Full Width) -->
    <div class="animate-on-scroll full-width-section property-manager-section">
        <div class="leasing-glass-card animate-on-scroll">
          <div class="leasing-bg-bars"></div>
          <div class="leasing-content">
            <div class="leasing-text-group">
              <h3 class="leasing-subtitle">{{ $t('home.businessSubtitle') }}</h3>
              <h2>{{ $t('home.businessTitle') }}</h2>
            </div>
            <button @click="showPopup = true" class="leasing-get-started-btn" @mouseenter="handleButtonHover" @mouseleave="handleButtonLeave">
              {{ $t('home.getStarted') }}
            </button>
          </div>
        </div>
    </div>

    <!-- 7. POPULAR LISTINGS SECTION -->
    <div class="content-container animate-on-scroll">
      <div class="popular-listings-header">
        <div class="popular-listings-title-group">
          <h2 class="section-title">{{ $t('home.popularListings') }}</h2>
          <p class="popular-listings-subtitle">{{ $t('home.topRatedHousing') }}</p>
        </div>
        <button class="popular-see-more-btn" @click="ToHouseReviewScore">
          <span>{{ $t('home.seeMore') }}</span>
        </button>
      </div>
      <div class="popular-listings-grid">
        <div v-if="popularListings.length === 0" class="popular-listings-empty">
          <p>{{ $t('home.noPopularListings') }}</p>
        </div>
        <div v-for="(item, index) in popularListings" :key="`popular-${item.houseId}-${item.houseLetId}-${index}`" class="popular-card" @click="goToHouseDetail(item.houseId || item.id, item.houseLetId)">
           <div class="pop-card-icon">
             <img :src="item.logo" :alt="item.name" @error="handleImageError">
           </div>
           <div class="pop-card-content">
             <h3>{{ item.name }}</h3>
             <p>{{ item.domain }}</p>
             <div class="pop-rating">
               <div class="stars">
                 <i 
                   v-for="n in 5" 
                   :key="n" 
                   class="el-icon-star-on" 
                   :class="getStarClasses(n, Math.round(item.average || parseFloat(item.rating) || 0), true)"
                 ></i>
               </div>
               <span v-if="item.count === 0" class="rating-text">{{ $t('emptyState.notReviewedYet') }}</span>
               <span v-else class="rating-text">{{ item.rating || '0.0' }} ({{ item.count || 0 }})</span>
             </div>
           </div>
        </div>
      </div>
    </div>

    <!-- POPUP MODAL -->
    <transition name="modal-fade">
      <div v-if="showPopup" class="modal-backdrop" @click.self="showPopup = false">
        <div class="modal-glass">
          <div class="modal-header">
            <h3>{{ $t('home.partnerTitle') }}</h3>
            <button class="close-btn" @click="showPopup = false">&times;</button>
          </div>
          <p class="modal-subtitle">{{ $t('home.partnerSubtitle') }}</p>
          
          <form @submit.prevent="handleSubmit" class="modern-form">
            <div class="input-group">
              <input v-model="form.name" type="text" required :placeholder="$t('home.fullName')" />
            </div>
            <div class="input-group">
              <input v-model="form.email" type="email" required :placeholder="$t('home.workEmail')" />
            </div>
            <div class="checkbox-group">
              <input type="checkbox" id="sub" v-model="form.subscribe" />
              <label for="sub">{{ $t('home.keepUpdated') }}</label>
            </div>
            <button type="submit" class="submit-btn-full">{{ $t('home.submitRequest') }}</button>
          </form>
        </div>
      </div>
    </transition>

    <!-- 8. TRUSTPILOT HERO BANNER -->
    <div class="animate-on-scroll full-width-section">
      <div class="tp-hero-banner">
        <div class="tp-hero-content">
          <h1>{{ $t('home.helpMillions') }}</h1>
          <p>{{ $t('home.shareExperience') }}</p>
          <div class="tp-hero-actions">
            <button class="tp-black-btn" @click="ToLogin">{{ $t('home.loginOrSignUp') }}</button>
          </div>
        </div>
        <div class="tp-hero-images">
          <div class="hero-image-group">
            <div
              v-for="(imgIndex, i) in heroImageIndices"
              :key="`banner-wrapper-${i}`"
              class="hero-img-card-wrapper"
            >
              <div
                class="hero-img-card staggered-image"
                :key="`img-${imgIndex}-${i}`"
                :style="{ animationDelay: `${i * 0.8}s` }"
              >
                <img 
                  :src="heroBannerImages[imgIndex]?.[i] || heroBannerImages[0]?.[i] || ''" 
                  :alt="`Hero image ${i + 1}`"
                  @error="handleHeroImageError"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 9. RECENT REVIEWS SECTION (Moved beneath login/signup card) -->
    <div class="content-container animate-on-scroll">
      <div class="reviews-header">
        <h2 class="section-title">{{ $t('home.recentReviews') }}</h2>
        <div class="reviews-nav" v-if="recentReviews.length > 0">
          <button class="nav-arrow left" @click="prevReviewPage" :disabled="reviewPageStart === 0">
            <i class="el-icon-arrow-left"></i>
          </button>
          <button class="nav-arrow right" @click="nextReviewPage" :disabled="reviewPageEnd >= recentReviews.length">
            <i class="el-icon-arrow-right"></i>
          </button>
        </div>
      </div>
      <div v-if="recentReviews.length > 0" class="reviews-grid">
        <div v-for="(review, index) in displayedReviews" :key="`review-${review.id || 'unknown'}-${index}`" class="review-card" @click="viewReviewDetails(review.id, review.rating, review)">
          <div class="review-header">
            <div class="reviewer-avatar-circle" :style="{ backgroundColor: getAvatarColor(review.name) }">
              {{ getInitials(review.name) }}
            </div>
            <div class="reviewer-info">
              <span class="reviewer-name">{{ review.name || $t('reviews.anonymous') }}</span>
              <div class="stars">
                <i
                  v-for="n in 5"
                  :key="n"
                  class="el-icon-star-on"
                  :class="getStarClasses(n, review.rating)"
                ></i>
              </div>
            </div>
          </div>
          <p class="review-text">{{ review.text || $t('reviews.noContent') }}</p>
          <div class="review-footer">
            <div class="review-company">
              <span>{{ review.company || 'N/A' }}</span>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="reviews-empty">
        <p>{{ $t('home.noRecentReviews') }}</p>
        <p class="reviews-empty-subtitle">{{ $t('home.beFirstReview') }}</p>
      </div>
    </div>

    <!-- Starbucks giveaway hero — live campaign progress + expandable rules -->
    <div class="animate-on-scroll starbucks-section">
      <div class="starbucks-carousel-wrapper">
        <article class="starbucks-hero-card" @click.stop>
          <div class="sb-hero-main">
            <h2 class="sb-hero-title">
              {{ $t('home.rewardTitlePrefix') }} <span class="sb-hero-highlight">$50 HKD</span> {{ $t('home.rewardTitleSuffix') }}
            </h2>
            <p class="sb-hero-lead">
              {{ $t('home.rewardLeadPrefix') }} <strong>{{ rewardLimitDisplay }}</strong> {{ $t('home.rewardLeadSuffix') }}
            </p>
            <div class="sb-progress-block">
              <div class="sb-progress-label-row">
                <span v-if="rewardCampaignLoading" class="sb-progress-label">{{ $t('home.loadingProgress') }}</span>
                <template v-else-if="rewardCampaign">
                  <span class="sb-progress-label">{{ rewardCampaignClaimedLabel }}</span>
                  <span v-if="rewardCampaign.rewardClosed" class="sb-progress-pill">{{ $t('home.roundFull') }}</span>
                </template>
                <span v-else class="sb-progress-label">{{ $t('home.campaignStatsError') }}</span>
              </div>
              <div
                class="sb-progress-track"
                role="progressbar"
                :aria-valuenow="rewardCampaignFillPct"
                aria-valuemin="0"
                aria-valuemax="100"
              >
                <div class="sb-progress-fill" :style="{ width: rewardCampaignFillPct + '%' }"></div>
              </div>
            </div>
            <button type="button" class="sb-btn sb-btn-viewmore" @click.stop="toggleGiveawayDetails">
              {{ giveawayDetailsOpen ? $t('home.showLess') : $t('home.viewMore') }}
            </button>
            <transition name="sb-fade">
              <div v-if="giveawayDetailsOpen" class="sb-details">
                <p class="sb-details-intro">{{ $t('home.howItWorks') }}</p>
                <ul class="sb-details-list">
                  <li>{{ $t('home.rewardStep1') }}</li>
                  <li>{{ $t('home.rewardStep2') }}</li>
                  <li>{{ $t('home.rewardStep3') }}</li>
                </ul>
                <p class="sb-details-foot">
                  {{ $t('home.rewardFoot') }}
                </p>
                <button type="button" class="sb-btn sb-btn-secondary" @click.stop="goGiveawayNextStep">
                  {{ giveawayNextStepLabel }}
                </button>
              </div>
            </transition>
          </div>
          <div class="sb-hero-logo-wrap" aria-hidden="true">
            <div class="sb-hero-logo-ring">
              <img src="svg/starbucks_logo_icon_170704.svg" alt="" />
            </div>
          </div>
        </article>
      </div>
    </div>

    <!-- FOOTER -->
    <footer class="modern-footer">
      <div class="footer-grid">
        <div class="footer-brand">
          <h3>Sparrow</h3>
          <p>{{ $t('home.footerTagline') }}</p>
          <div class="social-icons">
            <a href="#"><i class="fa fa-facebook"></i></a>
            <a href="#"><i class="fa fa-twitter"></i></a>
            <a href="#"><i class="fa fa-instagram"></i></a>
            <a href="#"><i class="fa fa-linkedin"></i></a>
          </div>
        </div>
        <div class="footer-links">
          <h4>{{ $t('home.platform') }}</h4>
          <a href="#">{{ $t('home.features') }}</a>
          <a href="#">{{ $t('app.discover') }}</a>
          <a href="#">{{ $t('home.community') }}</a>
          <a href="#">{{ $t('home.support') }}</a>
        </div>
        <div class="footer-links">
          <h4>{{ $t('home.company') }}</h4>
          <a href="#">{{ $t('home.aboutUs') }}</a>
          <a href="#">{{ $t('home.contactUs') }}</a>
          <a href="#">{{ $t('home.terms') }}</a>
          <a href="#">{{ $t('home.privacy') }}</a>
        </div>
        <div class="footer-subscribe">
          <h4>{{ $t('home.stayUpdated') }}</h4>
          <p>{{ $t('home.subscribeText') }}</p>
          <div class="sub-input">
            <input type="email" v-model="email" placeholder="email@university.edu" />
            <button @click="subscribe"><i class="el-icon-arrow-right"></i></button>
          </div>
        </div>
      </div>
    </footer>

  </div>
</template>

<script>
import Swal from 'sweetalert2';
import axios from 'axios';
import { hasValidStoredToken } from '@/utils/auth';
    
export default {
    data() {
    return {
      showPopup: false,
      searchText: '',
        email: '',
      selectedFilter: 'all',
      selectedOption: 'all',
      filters: [
        { key: 'all', labelKey: 'home.filters.all' },
        { key: 'company', labelKey: 'home.filters.companies' },
        { key: 'university', labelKey: 'home.filters.universities' }
      ],
      currentCardIndex: 0,
      recentReviews: [],
      reviewPageStart: 0,
      reviewsPerPage: 8,
      cards: [
        {
          titleKey: 'home.heroCards.trusted.title',
          subtitleKey: 'home.heroCards.trusted.subtitle',
          image: "https://images.unsplash.com/photo-1555854877-bab0e564b8d5?auto=format&fit=crop&w=1200&q=80"
        },
        {
          titleKey: 'home.heroCards.nest.title',
          subtitleKey: 'home.heroCards.nest.subtitle',
          image: "https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?auto=format&fit=crop&w=1200&q=80"
        }
      ],
      featureCards: [
        {
          icon: 'el-icon-user-solid',
          iconColor: 'blue',
          titleKey: 'home.featureCards.anonymous.title',
          textKey: 'home.featureCards.anonymous.text',
          ref: 'card1'
        },
        {
          icon: 'el-icon-s-data',
          iconColor: 'purple',
          titleKey: 'home.featureCards.rating.title',
          textKey: 'home.featureCards.rating.text',
          ref: 'card2'
        },
        {
          icon: 'el-icon-circle-check',
          iconColor: 'green',
          titleKey: 'home.featureCards.verified.title',
          textKey: 'home.featureCards.verified.text',
          ref: 'card3'
        },
        {
          icon: 'el-icon-chat-line-square',
          iconColor: 'orange',
          titleKey: 'home.featureCards.community.title',
          textKey: 'home.featureCards.community.text',
          ref: 'card4'
        }
      ],
      universities: ['HKU', 'CUHK', 'HKUST', 'PolyU', 'CityU', 'HKBU', 'EdUHK', 'LingnanU'],
      categories: [
        { id: 'dorms', labelKey: 'home.categories.dorms', icon: 'el-icon-school', filter: 'all' },
        { id: 'apartments', labelKey: 'home.categories.apartments', icon: 'el-icon-house', filter: 'all' },
        { id: 'sublets', labelKey: 'home.categories.sublets', icon: 'el-icon-time', filter: 'all' },
        { id: 'roommates', labelKey: 'home.categories.roommates', icon: 'el-icon-user', filter: 'all' },
        { id: 'companies', labelKey: 'home.categories.companies', icon: 'el-icon-office-building', filter: 'company' },
        { id: 'universities', labelKey: 'home.categories.universities', icon: 'el-icon-school', filter: 'university' },
        { id: 'studios', labelKey: 'home.categories.studios', icon: 'el-icon-home', filter: 'all' },
        { id: 'shared', labelKey: 'home.categories.shared', icon: 'el-icon-user-solid', filter: 'all' }
      ],
      form: { name: '', email: '', subscribe: false },
      interval: null,
      bannerInterval: null,
      observer: null,
      heroBannerIndex: 0,
      heroImageIndices: [0, 1, 2],
      heroBannerImages: [
        [
          "https://images.unsplash.com/photo-1555854877-bab0e564b8d5?auto=format&fit=crop&w=1200&q=100",
          "https://images.unsplash.com/photo-1574362848149-11496d93a7c7?auto=format&fit=crop&w=1200&q=100",
          "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&w=1200&q=100"
        ],
        [
          "https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?auto=format&fit=crop&w=1200&q=100",
          "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?auto=format&fit=crop&w=1200&q=100",
          "https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?auto=format&fit=crop&w=1200&q=100"
        ],
        [
          "https://images.unsplash.com/photo-1541829070764-84a7d30dd3f3?auto=format&fit=crop&w=1200&q=100",
          "https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=1200&q=100",
          "https://images.unsplash.com/photo-1554995207-c18c203602cb?auto=format&fit=crop&w=1200&q=100"
        ]
      ],
      REVIEWS_CONFIG: {
        HOUSES_TO_CHECK: 20,
        MAX_REVIEWS: 50,
        TIMEOUT: 10000
      },
      DEFAULT_SEARCH_PARAMS: {
        lat: 22.302711,
        lon: 114.177216,
        status: 0,
        selectedRegion: '',
        selectedDistrict: '',
        selectedArea: 'ALL AREAS',
        selectedULtype: 0,
        selectedType: 'any',
        selectedBedroom: 'any',
        selectedBathrooms: 'any',
        selectedRoommates: 'any',
        selectedFloorRoommates: 'any',
        selectedFloor: 'any'
      },
      topRowPath: 'M -10 50 C 10 50 10 25 30 25 H 110',
      bottomRowPath: 'M -10 50 C 10 50 10 75 30 75 H 110',
      verticalPath: 'M 20 35 V 65',
      popularListings: [],
      rewardCampaign: null,
      rewardCampaignLoading: false,
      giveawayDetailsOpen: false,
      name: "QuadrantLayout",
      company_email: '',
       activeColor: 'red'
    };
  },
  computed: {
    currentCard() {
      const card = this.cards[this.currentCardIndex];
      return {
        ...card,
        title: this.$t(card.titleKey),
        subtitle: this.$t(card.subtitleKey)
      };
    },
    splitTitle() {
      return this.currentCard.title.split('');
    },
    displayedReviews() {
      return this.recentReviews.slice(this.reviewPageStart, this.reviewPageEnd);
    },
    reviewPageEnd() {
      return this.reviewPageStart + this.reviewsPerPage;
    },
    navButtons() {
      return [
        { direction: 'prev', icon: 'el-icon-arrow-left', action: this.prevCard },
        { direction: 'next', icon: 'el-icon-arrow-right', action: this.nextCard }
      ];
    },
    rewardCampaignFillPct() {
      const c = this.rewardCampaign;
      if (!c || !c.rewardLimit) return 0;
      return Math.min(100, Math.round((c.approvedCount / c.rewardLimit) * 100));
    },
    rewardCampaignClaimedLabel() {
      const c = this.rewardCampaign;
      if (!c) return '';
      return this.$t('home.rewardsClaimed', { approved: c.approvedCount, limit: c.rewardLimit });
    },
    rewardLimitDisplay() {
      if (this.rewardCampaignLoading) return '…';
      const c = this.rewardCampaign;
      if (c && c.rewardLimit != null) return String(c.rewardLimit);
      return '50';
    },
    giveawayNextStepLabel() {
      return this.canUseProtectedHouseApis() ? this.$t('home.openProfileChecklist') : this.$t('home.signInToParticipate');
    }
  },
  mounted() {
    this.setupIntersectionObserver();
    this.setupScrollEffects();
    this.interval = setInterval(this.nextCard, 8000);
    this.startBannerRotation();
    this.fetchRewardCampaignSnapshot();
    this.fetchRecentReviews();
    this.fetchPopularListings();
    
    this.$nextTick(() => {
      this.updateSvgPaths();
      window.addEventListener('resize', this.updateSvgPaths);
      setTimeout(() => this.updateSvgPaths(), 100);
    });
  },
  beforeDestroy() {
    if (this.interval) clearInterval(this.interval);
    if (this.bannerInterval) clearInterval(this.bannerInterval);
    if (this.observer) this.observer.disconnect();
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.updateSvgPaths);
  },
  methods: {
    canUseProtectedHouseApis() {
      return hasValidStoredToken();
    },
    fetchRewardCampaignSnapshot() {
      this.rewardCampaignLoading = true;
      axios
        .get('/api/reward/campaign')
        .then((res) => {
          const root = res && res.data;
          const d = root && Object.prototype.hasOwnProperty.call(root, 'data') ? root.data : root;
          if (
            d &&
            typeof d.approvedCount === 'number' &&
            typeof d.rewardLimit === 'number' &&
            typeof d.rewardClosed === 'boolean'
          ) {
            this.rewardCampaign = d;
          }
        })
        .catch(() => {
          this.rewardCampaign = null;
        })
        .finally(() => {
          this.rewardCampaignLoading = false;
        });
    },
    toggleGiveawayDetails() {
      this.giveawayDetailsOpen = !this.giveawayDetailsOpen;
    },
    goGiveawayNextStep() {
      if (!this.canUseProtectedHouseApis()) {
        this.ToLogin();
        return;
      }
      this.$router.push('/userProfile').catch(() => {});
    },
    buildGuestListing(house, index = 0) {
      const houseId = house.houseId || house.id || house.house_id || null;
      const houseLetId = house.houseLetId || house.houseletId || house.houselet_id || house.houseLet_id || null;
      const fallbackImages = [
        'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?auto=format&fit=crop&w=800&q=90',
        'https://images.unsplash.com/photo-1493809842364-78817add7ffb?auto=format&fit=crop&w=800&q=90',
        'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?auto=format&fit=crop&w=800&q=90',
        'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&w=800&q=90'
      ];
      const average = Number(house.average || house.rating || 0) || 0;
      const reviewCount = parseInt(house.rateNum || house.reviewCount || house.count || 0, 10) || 0;

      return {
        id: `${houseId || 'house'}-${houseLetId || index}`,
        houseId,
        houseLetId,
        name: house.name || house.houseName || house.house_name || `House ${houseId || index + 1}`,
        logo: house.logo || house.houseLogo || fallbackImages[index % fallbackImages.length],
        domain: house.type || house.area || house.region || 'Housing',
        rating: average > 0 ? average.toFixed(1) : 'N/A',
        count: reviewCount,
        average
      };
    },
    ToHouseReviewScore() {
        if (this.$route.path !== '/recommendHouse') {
        this.$router.push({ path: '/recommendHouse', query: { keyword: this.searchText } });
    }
  },
      ToHouseReviewScoreu() {
        if (this.$route.path !== '/recommendHouseUniversity') {
        this.$router.push({ path: '/recommendHouseUniversity', query: { keyword: this.searchText } });
    }
  },
      ToHouseReviewScorec() {
        if (this.$route.path !== '/recommendHouseCompany') {
        this.$router.push({ path: '/recommendHouseCompany', query: { keyword: this.searchText } });
    }
  },
    changeColor() {
      console.log("yes");
    },
    search() {
      console.log("搜索已启动，条件为:", this.selectedOption, this.searchText);
    },
    subscribe() {
      console.log('Subscribe with email:', this.email);
      axios.get(`/api/save/subscribe?email=${this.email}`)
      .then(response => {
        console.log(response)
         Swal.fire({
           title: 'Success',
           text: 'Success Subscribe',
           icon: 'success',
           confirmButtonText: 'OK'
         })
      })
      .catch(error => {
        console.log('读取文件失败：' + error);
      });
    },
    handleSubmit() {
  if (!this.form.name || !this.form.email) {
    alert('Please fill in both name and email');
    return;
  }

  const data = {
    subname: this.form.name,
    email: this.form.email,
    subscribe: this.form.subscribe
  };

      axios.post('/api/save/companyemail', data, {
    headers: {
          'Content-Type': 'application/json'
    }
  })
    .then(response => {
        console.log('Success:', response.data);
        this.showPopup = false;
        Swal.fire({
          title: 'Success!',
          text: 'We will contact you promptly!',
          icon: 'success',
          confirmButtonText: 'OK'
        });
    })
    .catch(error => {
      console.error('Error:', error.response ? error.response.data : error.message);
      alert('Submission failed. Please try again.');
    });
},
    companyemail() {
      console.log('Contact Us with email:', this.company_email);
      axios.get(`/api/save/companyemail?email=${this.company_email}`)
      .then(response => {
        console.log(response)
         Swal.fire({
           title: 'Success',
           text: 'We will contact you shortly~',
           icon: 'success',
           confirmButtonText: 'OK'
         })
      })
      .catch(error => {
        console.log('读取文件失败：' + error);
      });
    },
    nextCard() {
      this.currentCardIndex = (this.currentCardIndex + 1) % this.cards.length;
    },
    prevCard() {
      this.currentCardIndex = (this.currentCardIndex - 1 + this.cards.length) % this.cards.length;
    },
    updateSvgPaths() {
      this.$nextTick(() => {
        if (!this.$refs.rightGrid) return;
        
        const cardRefs = ['card1', 'card2', 'card3', 'card4'];
        const cards = cardRefs.map(ref => {
          const cardEl = this.$refs[ref];
          if (!cardEl) return null;
          const domEl = cardEl.$el || cardEl;
          if (domEl && typeof domEl.querySelector === 'function') {
            const featureCard = domEl.querySelector('.feature-card');
            return featureCard ? featureCard.getBoundingClientRect() : domEl.getBoundingClientRect();
          }
          if (domEl && typeof domEl.getBoundingClientRect === 'function') {
            return domEl.getBoundingClientRect();
          }
          return null;
        }).filter(Boolean);

        if (cards.length !== 4) return;

        const grid = this.$refs.rightGrid;
        const gridRect = grid.getBoundingClientRect();
        const gridLeft = gridRect.left;
        const gridTop = gridRect.top;
        const gridWidth = gridRect.width;
        const gridHeight = gridRect.height;

        const cardCenters = cards.map(cardRect => ({
          x: (cardRect.left - gridLeft) + (cardRect.width / 2),
          y: (cardRect.top - gridTop) + (cardRect.height / 2)
        }));

        const topRowY = ((cardCenters[0].y + cardCenters[1].y) / 2 / gridHeight) * 100;
        const bottomRowY = ((cardCenters[2].y + cardCenters[3].y) / 2 / gridHeight) * 100;
        
        const entryY = 50;
        const startX = -10;
        const topCurveEndX = ((cards[0].left - gridLeft) / gridWidth) * 100;
        const bottomCurveEndX = ((cards[2].left - gridLeft) / gridWidth) * 100;
        const topEndX = ((cards[1].right - gridLeft) / gridWidth) * 100 + 5;
        const bottomEndX = ((cards[3].right - gridLeft) / gridWidth) * 100 + 5;
        const controlX = 10;
        
        this.topRowPath = `M ${startX} ${entryY} C ${controlX} ${entryY} ${controlX} ${topRowY} ${topCurveEndX} ${topRowY} H ${topEndX}`;
        this.bottomRowPath = `M ${startX} ${entryY} C ${controlX} ${entryY} ${controlX} ${bottomRowY} ${bottomCurveEndX} ${bottomRowY} H ${bottomEndX}`;
        
        const verticalX = Math.min(topCurveEndX, bottomCurveEndX) - 5;
        const verticalTop = topRowY + 3;
        const verticalBottom = bottomRowY - 3;
        this.verticalPath = `M ${verticalX} ${verticalTop} V ${verticalBottom}`;
      });
    },
    navigateTo(path, filter = 'all') {
      this.selectedFilter = filter;
      this.selectedOption = filter;
      const query = this.searchText ? { keyword: this.searchText } : {};
      if (this.$route.path !== path || this.$route.query.keyword !== this.searchText) {
        this.$router.push({ path, query }).catch(() => {});
      }
    },
    setFilter(key) {
      const paths = {
        all: '/recommendHouse',
        university: '/recommendHouseUniversity',
        company: '/recommendHouseCompany'
      };
      this.navigateTo(paths[key] || '/recommendHouse', key);
    },
    goToAddHouse() {
      this.$router.push('/AddHouse').catch(() => {});
    },
    handleSearch() {
      const paths = {
        all: '/recommendHouse',
        university: '/recommendHouseUniversity',
        company: '/recommendHouseCompany'
      };
      const path = paths[this.selectedFilter] || '/recommendHouse';
      this.navigateTo(path, this.selectedFilter);
    },
    goToHouseDetail(houseId, houseLetId) {
      if (houseId && houseLetId) {
        this.$router.push({ 
          path: '/HouseReviewScore', 
          query: { houseId: houseId, houseLetId: houseLetId } 
        });
      } else {
        this.ToHouseReviewScore();
      }
    },
    handleImageError(event) {
      if (event.target.src && !event.target.src.includes('default')) {
        const apartmentImages = [
          'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?auto=format&fit=crop&w=800&q=90',
          'https://images.unsplash.com/photo-1493809842364-78817add7ffb?auto=format&fit=crop&w=800&q=90',
          'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?auto=format&fit=crop&w=800&q=90',
          'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&w=800&q=90',
          'https://images.unsplash.com/photo-1484154218962-a197022b5858?auto=format&fit=crop&w=800&q=90',
          'https://images.unsplash.com/photo-1505843513577-22bb7d21e455?auto=format&fit=crop&w=800&q=90',
          'https://images.unsplash.com/photo-1512918728675-ed5a9ecdebfd?auto=format&fit=crop&w=800&q=90',
          'https://images.unsplash.com/photo-1507089947368-19c1da9775ae?auto=format&fit=crop&w=800&q=90'
        ];
        const cardIndex = Array.from(event.target.closest('.popular-listings-grid')?.children || []).indexOf(event.target.closest('.popular-card')) || 0;
        event.target.src = apartmentImages[cardIndex % apartmentImages.length];
        event.target.onerror = null;
      }
    },
    handleHeroImageError(event) {
      const heroFallbackImages = [
        'https://images.unsplash.com/photo-1555854877-bab0e564b8d5?auto=format&fit=crop&w=1200&q=100',
        'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?auto=format&fit=crop&w=1200&q=100',
        'https://images.unsplash.com/photo-1541829070764-84a7d30dd3f3?auto=format&fit=crop&w=1200&q=100'
      ];
      
      const imgElement = event.target;
      const wrapper = imgElement.closest('.hero-img-card-wrapper');
      if (wrapper) {
        const siblings = Array.from(wrapper.parentElement?.children || []);
        const index = siblings.indexOf(wrapper);
        if (index >= 0 && index < heroFallbackImages.length) {
          imgElement.src = heroFallbackImages[index];
          imgElement.onerror = null;
          return;
        }
      }
      
      imgElement.src = heroFallbackImages[0];
      imgElement.onerror = null;
    },
    setupIntersectionObserver() {
      const options = { threshold: 0.15, rootMargin: "0px" };
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
            if (entry.target.classList.contains('feature-card-wrapper')) {
              const rightGrid = entry.target.closest('.right-grid');
              if (rightGrid) {
                rightGrid.classList.add('in-view');
                this.updateSvgPaths();
              }
            }
            this.observer.unobserve(entry.target);
          }
        });
      }, options);

      this.$nextTick(() => {
        document.querySelectorAll('.animate-on-scroll').forEach(el => {
          this.observer.observe(el);
        });
      });
    },
    setupScrollEffects() {
      let ticking = false;
      
      const updateOnScroll = () => {
        const scrollY = window.scrollY;
        const windowHeight = window.innerHeight;
        
        const cardBgImage = this.$el.querySelector('.visual-bg');
        if (cardBgImage) {
          const parallaxY = scrollY * 0.15;
          cardBgImage.style.transform = `translateY(${parallaxY}px) scale(1.05)`;
        }
        
        const orbs = this.$el.querySelectorAll('.floating-orb');
        orbs.forEach((orb, index) => {
          const speed = 0.1 + (index * 0.05);
          const yOffset = scrollY * speed;
          orb.style.transform = `translateY(${yOffset}px)`;
        });
        
        const shapes = this.$el.querySelectorAll('.shape');
        shapes.forEach((shape, index) => {
          const speed = 0.05 + (index * 0.03);
          const yOffset = scrollY * speed;
          const rotation = scrollY * 0.1;
          shape.style.transform = `translateY(${yOffset}px) rotate(${rotation}deg)`;
        });
        
        if (this.$refs.heroSection) {
          const heroOffset = scrollY * 0.2;
          this.$refs.heroSection.style.transform = `translateY(${heroOffset}px)`;
        }
        
        const featureCards = this.$el.querySelectorAll('.feature-card-wrapper.in-view');
        featureCards.forEach((card, index) => {
          const rect = card.getBoundingClientRect();
          if (rect.top < windowHeight && rect.bottom > 0) {
            const progress = Math.max(0, Math.min(1, (windowHeight - rect.top) / (windowHeight + rect.height)));
            const translateY = (1 - progress) * 20 * (index % 2 === 0 ? 1 : 0.5);
            card.style.transform = `translateY(${-translateY}px)`;
          }
        });
        
        ticking = false;
      };
      
      this.handleScroll = () => {
        if (!ticking) {
          window.requestAnimationFrame(updateOnScroll);
          ticking = true;
        }
      };
      
      window.addEventListener('scroll', this.handleScroll, { passive: true });
      updateOnScroll();
    },
    handleButtonHover(e) {
      const btn = e.currentTarget;
      const rect = btn.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      btn.style.transform = 'scale(1.05)';
      
      const ripple = btn.querySelector('.btn-ripple');
      if (ripple) {
        ripple.style.left = `${x}px`;
        ripple.style.top = `${y}px`;
        ripple.classList.add('active');
        setTimeout(() => ripple.classList.remove('active'), 600);
      }
    },
    handleButtonLeave(e) {
      const btn = e.currentTarget;
      btn.style.transform = '';
    },
    showAlert(type, title, text) {
      const icons = {
        success: 'success',
        error: 'error',
        warning: 'warning',
        info: 'info'
      };
      return Swal.fire({
        title,
        text,
        icon: icons[type] || 'info',
        confirmButtonColor: '#3bc2ff'
      });
    },
    async submitForm(endpoint, data, successMsg, resetCallback) {
      try {
        const response = await axios.post(endpoint, data, {
          headers: { 'Content-Type': 'application/json' }
        });
        console.log('Success:', response.data);
        if (resetCallback) resetCallback();
        await this.showAlert('success', 'Success!', successMsg);
        return response;
      } catch (error) {
        console.error('Error:', error.response ? error.response.data : error.message);
        await this.showAlert('error', 'Submission Failed', 'Please try again later.');
        throw error;
      }
    },
    startBannerRotation() {
      this.bannerInterval = setInterval(() => {
        setTimeout(() => {
          this.$set(this.heroImageIndices, 0, (this.heroImageIndices[0] + 1) % this.heroBannerImages.length);
        }, 0);
        setTimeout(() => {
          this.$set(this.heroImageIndices, 1, (this.heroImageIndices[1] + 1) % this.heroBannerImages.length);
        }, 250);
        setTimeout(() => {
          this.$set(this.heroImageIndices, 2, (this.heroImageIndices[2] + 1) % this.heroBannerImages.length);
        }, 500);
        
        this.heroBannerIndex = (this.heroBannerIndex + 1) % this.heroBannerImages.length;
      }, 5000);
    },
    ToLogin() {
      this.$router.push('/LogReg');
    },
    async fetchRecentReviews() {
      try {
        console.log('🔍 Fetching recent reviews...');

        const houses = await this.searchHouses();
        
        const validHouses = houses.filter(house => {
          const name = (house.name || house.houseName || '').toLowerCase();
          return !name.includes('test');
        });

        if (!this.canUseProtectedHouseApis()) {
          console.log('🔒 Skipping recent review enrichment for guests');
          this.recentReviews = [];
          return;
        }

        const reviewPromises = validHouses.slice(0, 5).map(async (house) => {
          try {
            const houseId = house.houseId || house.id;
            let houseLetId = house.houseLetId || house.houseletId;
            
            if (!houseId) {
              console.warn(`Skipping house - missing houseId:`, house);
              return [];
            }
            
            if (!houseLetId) {
              try {
                const roomsResponse = await axios.get('/api/search/room', {
                  params: {
                    houseId: houseId,
                    pageNo: 1,
                    pageSize: 10
                  },
                  timeout: 5000
                });
                const rooms = Array.isArray(roomsResponse.data) 
                  ? roomsResponse.data 
                  : (roomsResponse.data?.data || []);
                if (rooms.length > 0) {
                  // Fetch reviews from ALL rooms, not just the first one
                  const allRoomReviews = [];
                  for (const room of rooms) {
                    const roomId = room.id || room.houseLetId || room.houseletId;
                    if (!roomId) continue;
                    try {
                      const resp = await axios.get('/api/house/reviewInfo', {
                        params: { houseId, houseLetId: roomId },
                        timeout: 5000
                      });
                      let revs = [];
                      if (Array.isArray(resp.data)) revs = resp.data;
                      else if (resp.data && Array.isArray(resp.data.data)) revs = resp.data.data;
                      allRoomReviews.push(...revs.map(r => ({ ...r, houseId, houseLetId: roomId })));
                    } catch(e) { /* skip failed room */ }
                  }
                  return allRoomReviews;
                }
              } catch (e) {
                return [];
              }
            }
            
            if (!houseLetId) {
              console.warn(`Skipping house - could not get houseLetId:`, house);
              return [];
            }
            
        const response = await axios.get('/api/house/reviewInfo', {
              params: {
                houseId: houseId,
                houseLetId: houseLetId
              },
              timeout: 5000
            });
            
            let reviews = [];
            if (Array.isArray(response.data)) {
              reviews = response.data;
            } else if (response.data && Array.isArray(response.data.data)) {
              reviews = response.data.data;
            }
            
            return reviews.map(review => ({
              ...review,
              houseId: houseId,
              houseLetId: houseLetId
            }));
          } catch (error) {
            if (error.response && error.response.status === 401) {
              console.error(`❌ [ReviewInfo 401] Unauthorized access for house ${house.houseId || house.id}`);
            } else if (error.response && error.response.status !== 500) {
              console.warn(`⚠️  [ReviewInfo] Could not fetch reviews for house ${house.houseId || house.id}:`, error.message);
            } else {
              console.error(`❌ [ReviewInfo] Server error for house ${house.houseId || house.id}`);
            }
            return [];
          }
        });

        const reviewArrays = await Promise.all(reviewPromises);
        const allReviews = reviewArrays.flat();
        
        allReviews.sort((a, b) => {
          const dateA = this.parseDate(a.dateTime || a.createdAt);
          const dateB = this.parseDate(b.dateTime || b.createdAt);
          return dateB.getTime() - dateA.getTime();
        });

        const recentReviews = allReviews.slice(0, 20);
        
        const formatted = this.formatReviews(recentReviews);
        console.log(`✅ Loaded ${formatted.length} recent reviews`);
        this.recentReviews = formatted;

      } catch (error) {
        console.error('❌ Error fetching reviews:', error);
        this.recentReviews = [];
      }
    },
    async fetchPopularListings() {
      try {
        console.log('🏠 Fetching popular listings...');

        const houses = await this.searchHouses();
        
        const validHouses = houses.filter(house => {
          const name = (house.name || house.houseName || '').toLowerCase();
          return !name.includes('test');
        });

        if (!this.canUseProtectedHouseApis()) {
          this.popularListings = validHouses
            .slice(0, 6)
            .map((house, index) => this.buildGuestListing(house, index));
          console.log(`✅ Loaded ${this.popularListings.length} guest-safe popular listings`);
          return;
        }

        const listingPromises = validHouses.slice(0, 10).map(async (house) => {
          try {
            const houseId = house.houseId || house.id;
            let houseLetId = house.houseLetId || house.houseletId;
            
            if (!houseId) return null;
            
            if (!houseLetId) {
              try {
                const roomsResponse = await axios.get('/api/search/room', {
                  params: {
                    houseId: houseId,
                    pageNo: 1,
                    pageSize: 1
                  },
                  timeout: 5000
                });
                const rooms = Array.isArray(roomsResponse.data) 
                  ? roomsResponse.data 
                  : (roomsResponse.data?.data || []);
                if (rooms.length > 0) {
                  houseLetId = rooms[0].id || rooms[0].houseLetId || rooms[0].houseletId;
                }
              } catch (e) {
                return null;
              }
            }
            
            if (!houseLetId) return null;
            
            const ratingResponse = await axios.get('/api/house/allinfo', {
              params: { houseId, houseLetId },
              timeout: 5000
            });
            
            let ratingData = {};
            if (ratingResponse.data && ratingResponse.data.data) {
              ratingData = ratingResponse.data.data;
            } else if (ratingResponse.data) {
              ratingData = ratingResponse.data;
            }
            
            const average = parseFloat(ratingData.average || 0);
            const rateNum = parseInt(ratingData.rateNum || 0) || 0;
            
            if (average <= 0) return null;
            
            let houseDetails = {};
            try {
              const houseResponse = await axios.get('/api/house', {
                params: { houseId },
                timeout: 5000
              });
              if (houseResponse.data && houseResponse.data.data) {
                houseDetails = houseResponse.data.data;
              } else if (houseResponse.data) {
                houseDetails = houseResponse.data;
              }
            } catch (e) {
              if (e.response && e.response.status === 401) {
                console.error(`❌ [HouseDetails 401] Unauthorized access for houseId ${houseId}`);
              } else {
                console.warn(`⚠️  [HouseDetails] Failed to fetch house details for ${houseId}:`, e.message);
              }
            }

            let houseletDetails = {};
            try {
              const houseletResponse = await axios.get('/api/house/let', {
                params: { houseletId: houseLetId },
                timeout: 5000
              });
              if (houseletResponse.data && houseletResponse.data.data) {
                houseletDetails = houseletResponse.data.data;
              } else if (houseletResponse.data) {
                houseletDetails = houseletResponse.data;
              }
            } catch (e) {
              console.warn('Failed to fetch houselet details', e);
            }

            const houseName = houseletDetails.houseLetName || houseDetails.name || `House ${houseId}`;
            
            const apartmentImage = 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?auto=format&fit=crop&w=800&q=90';

            return {
              id: `${houseId}-${houseLetId}`,
              houseId,
              houseLetId,
              name: houseName,
              logo: apartmentImage,
              domain: houseDetails.type || 'Housing',
              rating: average.toFixed(1),
              count: rateNum || 0,
              average: average
            };
          } catch (error) {
            if (error.response && error.response.status === 401) {
              console.error(`❌ [Rating 401] Unauthorized access for house ${house.id}`);
            } else {
              console.warn(`⚠️  [Rating] Could not fetch rating for house ${house.id}:`, error.message);
            }
            return null;
          }
        });

        const listings = await Promise.all(listingPromises);
        const validListings = listings.filter(listing => listing !== null);
        
        validListings.sort((a, b) => b.average - a.average);
        
        this.popularListings = validListings.slice(0, 6);
        
        console.log(`✅ Loaded ${this.popularListings.length} popular listings`);

      } catch (error) {
        console.error('❌ Error fetching popular listings:', error);
        this.popularListings = [];
      }
    },
    async searchHouses() {
      try {
        console.log('🔎 Searching for houses...');
        
        const response = await axios.post('/api/search/searchElliotFilter', {
          keyword: '',
          lat: 22.302711,
          lon: 114.177216,
          status: 0,
          selectedRegion: '',
          selectedDistrict: '',
          selectedArea: 'ALL AREAS',
          selectedULtype: 0,
          selectedType: 'any',
          selectedBedroom: 'any',
          selectedBathrooms: 'any',
          selectedRoommates: 'any',
          selectedFloorRoommates: 'any',
          selectedFloor: 'any'
        }, { timeout: 10000 });

        console.log('📦 Search response:', response.data);

        let houses = [];
        if (Array.isArray(response.data)) {
          houses = response.data;
        } else if (response.data && Array.isArray(response.data.data)) {
          houses = response.data.data;
        } else {
          console.warn('Unexpected response structure:', response.data);
          houses = [];
        }

        houses = houses.map(house => ({
          ...house,
          houseId: house.houseId || house.id || house.house_id,
          houseLetId: house.houseLetId || house.houseletId || house.houselet_id || house.houseLet_id,
          name: house.name || house.houseName || house.house_name || `House ${house.houseId || house.id}`
        }));

        console.log('✅ Parsed houses:', houses.length);
        if (houses.length > 0) {
          console.log('📋 Sample house:', houses[0]);
        }
        return houses;
      } catch (error) {
        console.error('❌ Search houses error:', error.message);
        if (error.response) {
          console.error('Response status:', error.response.status);
          console.error('Response data:', error.response.data);
        }
        return [];
      }
    },
    parseDate(dateTime) {
      if (!dateTime) return new Date(0);
      
      if (Array.isArray(dateTime) && dateTime.length >= 6) {
        return new Date(dateTime[0], dateTime[1] - 1, dateTime[2], dateTime[3] || 0, dateTime[4] || 0, dateTime[5] || 0, dateTime[6] || 0);
      }
      
      if (typeof dateTime === 'string') {
        const parsed = new Date(dateTime);
        return isNaN(parsed.getTime()) ? new Date(0) : parsed;
      }
      
      if (dateTime instanceof Date) return dateTime;
      
      return new Date(0);
    },
    compareReviews(a, b) {
      const dateA = this.parseDate(a.dateTime);
      const dateB = this.parseDate(b.dateTime);
      
      const timeA = dateA.getTime();
      const timeB = dateB.getTime();
      
      if (timeA > 0 && timeB > 0) return timeB - timeA;
      if (timeA > 0) return -1;
      if (timeB > 0) return 1;
      
      const idA = parseInt(String(a.id || ''), 10) || 0;
      const idB = parseInt(String(b.id || ''), 10) || 0;
      return idB - idA;
    },
    calculateAvgRating(review) {
      const scores = [
        review.safety, review.rentValue, review.location, review.utilities,
        review.cleanliness, review.internet, review.noiseLevel, review.roomSize,
        review.maintenance, review.roomMates
      ].filter(s => s != null && s > 0);
      
      if (scores.length > 0) {
        return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
      } else if (review.average != null) {
        return Math.round(review.average);
      } else if (review.rating != null) {
        return Math.round(review.rating);
      }
      return 0;
    },
    formatReviews(reviews) {
      if (!reviews || !Array.isArray(reviews)) {
        console.log('⚠️ formatReviews: Invalid input', reviews);
        return [];
      }
      
      console.log(`📝 Formatting ${reviews.length} reviews...`);
      
      const formatted = reviews
        .map((review, index) => {
          const avgRating = this.calculateAvgRating(review);
          
          const textContent = review.content || 
                            review.reviewText || 
                            review.text || 
                            review.comment || 
                            review.description || '';
          const normalizedText = String(textContent || '').trim();
          const fallbackSummaryParts = [];
          if (review.roomType) fallbackSummaryParts.push(`${review.roomType} room`);
          if (review.bedType) fallbackSummaryParts.push(review.bedType);
          if (review.monthlyRent != null && Number(review.monthlyRent) > 0) {
            fallbackSummaryParts.push(`HK$${review.monthlyRent}/month`);
          }
          if (review.stayDuration != null && Number(review.stayDuration) > 0) {
            fallbackSummaryParts.push(`${review.stayDuration} year(s) stay`);
          }
          const fallbackSummary = fallbackSummaryParts.join(' · ');
          const finalText = normalizedText.length > 0
            ? normalizedText
            : `Overall ${Math.max(1, avgRating)}/5.${fallbackSummary ? ` ${fallbackSummary}.` : ''}`;
          
          const uniqueId = review.id || review.rateId || review.reviewId || `review-${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`;
          
          return {
            id: uniqueId,
            name: review.name || review.userName || review.username || 'Anonymous',
            rating: Math.min(5, Math.max(1, avgRating)),
            text: finalText,
            company: review.company || review.houseLetName || review.houseName || 'N/A',
            companyLogo: review.companyLogo || review.houseLogo || null,
            content: finalText,
            avatar: review.avatar || review.userAvatar || null,
            houseId: review.houseId,
            houseLetId: review.houseLetId,
            roomType: review.roomType || null,
            bedType: review.bedType || null,
            tags: review.tags || null
          };
        });
      
      console.log(`✅ Formatted ${formatted.length} reviews successfully`);
      if (formatted.length > 0) {
        console.log(`📋 Sample formatted review:`, formatted[0]);
      }
      return formatted;
    },
    prevReviewPage() {
      if (this.reviewPageStart > 0) {
        this.reviewPageStart = Math.max(0, this.reviewPageStart - this.reviewsPerPage);
      }
    },
    nextReviewPage() {
      if (this.reviewPageEnd < this.recentReviews.length) {
        this.reviewPageStart = Math.min(
          this.recentReviews.length - this.reviewsPerPage,
          this.reviewPageStart + this.reviewsPerPage
        );
      }
    },
    getInitials(name) {
      if (!name) return 'A';
      const parts = name.trim().split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return name[0].toUpperCase();
    },
    getAvatarColor(name) {
      if (!name) return '#3bc2ff';
      const colors = [
        '#3bc2ff', '#9333ea', '#16a34a', '#ea580c', '#0284c7',
        '#dc2626', '#7c3aed', '#059669', '#d97706', '#0891b2'
      ];
      let hash = 0;
      for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
      }
      return colors[Math.abs(hash) % colors.length];
    },
    getStarClasses(starNumber, rating, alwaysGreen = false) {
      const isActive = starNumber <= rating;
      const classes = { active: isActive };
      if (isActive) {
        if (alwaysGreen || rating >= 4) {
          classes['star-green'] = true;
        } else if (rating <= 1) {
          classes['star-red'] = true;
        }
      }
      return classes;
    },
    viewReviewDetails(reviewId, rating, review = null) {
      if (!reviewId) {
        console.error("No review ID provided");
        return;
      }
      
      const reviewIdStr = String(reviewId);
      const isTemporaryId = reviewIdStr.startsWith('temp-') || reviewIdStr.startsWith('review-');
      const isNumericId = /^\d+$/.test(reviewIdStr);
      
      const hasHouseInfo = review && (review.houseId || review.houseLetId);
      
      if ((isTemporaryId || !isNumericId) && hasHouseInfo) {
        console.log("Review has temporary/invalid ID, navigating to house page instead:", review.houseId, review.houseLetId);
        this.goToHouseDetail(review.houseId, review.houseLetId);
        return;
      }
      
      if (isTemporaryId || !isNumericId) {
        console.warn("Cannot navigate to review with temporary/invalid ID:", reviewId);
        Swal.fire({
          icon: 'info',
          title: 'Review Not Available',
          text: 'This review does not have a valid ID. Please view it from the house page.',
          confirmButtonText: 'OK',
          confirmButtonColor: '#3bc2ff'
        });
        return;
      }
      
      console.log("Navigating to review:", reviewId, "with rating:", rating);
      this.$router.push({
        path: "/showReview",
        query: { 
          id: String(reviewId),
          expectedAverage: rating ? String(rating) : undefined
        }
      }).catch(err => {
        console.error("Navigation error:", err);
        if (hasHouseInfo) {
          console.log("Navigation failed, trying house page as fallback");
          this.goToHouseDetail(review.houseId, review.houseLetId);
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Navigation Error',
            text: 'Unable to navigate to the review. Please try again.',
            confirmButtonText: 'OK',
            confirmButtonColor: '#3bc2ff'
          });
        }
      });
    },
    handleCategoryClick(category) {
      if (category.filter) {
        this.setFilter(category.filter);
      } else {
        this.ToHouseReviewScore();
      }
    }
  }
};
</script>

<style scoped>
/* --- 0. GLOBAL RESET & VARIABLES --- */
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;600;800&display=swap');

.page-wrapper {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: #0f172a;
  background: #fdfdfd;
  overflow-x: hidden;
  overflow-y: auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  scroll-behavior: smooth;
  position: relative;
  perspective: 1000px;
}

/* 3D Background Container */
.bg-3d-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
  overflow: hidden;
}

/* Floating 3D Orbs */
.floating-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(40px);
  opacity: 0.4;
  animation: float3D 20s ease-in-out infinite;
  will-change: transform;
}

.orb-1 {
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(59, 194, 255, 0.3) 0%, transparent 70%);
  top: -10%;
  left: -10%;
  animation-delay: 0s;
}

.orb-2 {
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(147, 51, 234, 0.2) 0%, transparent 70%);
  top: 50%;
  right: -5%;
  animation-delay: -5s;
}

.orb-3 {
  width: 350px;
  height: 350px;
  background: radial-gradient(circle, rgba(22, 163, 74, 0.2) 0%, transparent 70%);
  bottom: 20%;
  left: 20%;
  animation-delay: -10s;
}

.orb-4 {
  width: 250px;
  height: 250px;
  background: radial-gradient(circle, rgba(234, 88, 12, 0.2) 0%, transparent 70%);
  top: 30%;
  right: 30%;
  animation-delay: -15s;
}

/* Particle Field */
.particle-field {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
}

.particle {
  position: absolute;
  width: 4px;
  height: 4px;
  background: #3bc2ff;
  border-radius: 50%;
  opacity: 0;
  animation: particleFloat linear infinite;
  pointer-events: none;
}

/* Geometric Shapes */
.geometric-shapes {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
}

.shape {
  position: absolute;
  opacity: 0.1;
  pointer-events: none;
}

.shape-1 {
  width: 200px;
  height: 200px;
  background: linear-gradient(135deg, rgba(59, 194, 255, 0.3), rgba(147, 51, 234, 0.3));
  clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
  top: 20%;
  right: 10%;
  animation: shapeRotate 30s linear infinite;
}

.shape-2 {
  width: 150px;
  height: 150px;
  background: linear-gradient(45deg, rgba(22, 163, 74, 0.2), rgba(59, 194, 255, 0.2));
  border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%;
  bottom: 30%;
  left: 15%;
  animation: shapeRotate 25s linear infinite reverse;
}

.shape-3 {
  width: 180px;
  height: 180px;
  background: linear-gradient(90deg, rgba(234, 88, 12, 0.2), rgba(147, 51, 234, 0.2));
  clip-path: polygon(25% 0%, 100% 0%, 75% 100%, 0% 100%);
  top: 50%;
  right: 25%;
  animation: shapeRotate 35s linear infinite;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}

/* --- 1. ANIMATION KEYFRAMES --- */
@keyframes clipDown {
  0% { clip-path: inset(0 0 100% 0); }
  100% { clip-path: inset(0 0 0 0); }
}

@keyframes slideDownText {
  0% { transform: translateY(-100%); opacity: 0; filter: blur(10px); }
  100% { transform: translateY(0); opacity: 1; filter: blur(0); }
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes scroll {
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
}

@keyframes shine {
  0% { left: -100%; opacity: 0; }
  20% { opacity: 0.5; }
  100% { left: 100%; opacity: 0; }
}

@keyframes slideUpFade {
  to { opacity: 1; transform: translateY(0); filter: blur(0); }
}

@keyframes slideDownChar {
  from { 
    transform: translateY(-20px) scale(0.95); 
    opacity: 0; 
  }
  to { 
    transform: translateY(0) scale(1); 
    opacity: 1; 
  }
}

@keyframes blurIn {
  from { 
    opacity: 0; 
    filter: blur(8px); 
    transform: translateY(10px);
  }
  to { 
    opacity: 1; 
    filter: blur(0); 
    transform: translateY(0);
  }
}

@keyframes floatOrb {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(30px, -30px) scale(1.1); }
}

@keyframes blink { 
  50% { opacity: 0; } 
}

@keyframes float3D {
  0%, 100% { 
    transform: translate3d(0, 0, 0) rotateY(0deg) rotateX(0deg); 
  }
  33% { 
    transform: translate3d(30px, -30px, 20px) rotateY(120deg) rotateX(10deg); 
  }
  66% { 
    transform: translate3d(-20px, 20px, -15px) rotateY(240deg) rotateX(-10deg); 
  }
}

@keyframes particleFloat {
  0% { 
    transform: translateY(0) translateX(0) scale(1);
    opacity: 0;
  }
  10% {
    opacity: 0.6;
  }
  90% {
    opacity: 0.6;
  }
  100% { 
    transform: translateY(-100vh) translateX(50px) scale(1.5);
    opacity: 0;
  }
}

@keyframes shapeRotate {
  from { transform: rotate(0deg) scale(1); }
  to { transform: rotate(360deg) scale(1.1); }
}

@keyframes ripple {
  0% {
    transform: scale(0);
    opacity: 1;
  }
  100% {
    transform: scale(4);
    opacity: 0;
  }
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 0.8;
  }
  50% {
    transform: scale(1.2);
    opacity: 1;
  }
}

@keyframes glow {
  0%, 100% {
    box-shadow: 0 0 20px rgba(59, 194, 255, 0.3);
  }
  50% {
    box-shadow: 0 0 40px rgba(59, 194, 255, 0.6);
  }
}

/* --- 3. HERO SECTION --- */
.hero-section {
  position: relative;
  min-height: 90vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 20px 32px;
  overflow: hidden;
  z-index: 1;
  will-change: transform;
  transition: transform 0.1s ease-out;
}

/* Background Columns Clip Animation */
.bg-columns {
  position: absolute;
  top: 0; left: 0; width: 100%; height: 100%;
  display: flex;
  z-index: 0;
}

.bg-col {
  flex: 1;
  background: #ffffff;
  border-right: 1px solid rgba(0,0,0,0.02);
  clip-path: inset(0 0 100% 0);
  animation: clipDown 1.2s cubic-bezier(0.22, 1, 0.36, 1) forwards;
}

.hero-orb {
  position: absolute;
  width: 600px;
  height: 600px;
  background: radial-gradient(circle, rgba(59,194,255,0.15) 0%, rgba(59,194,255,0) 70%);
  top: 10%;
  left: 20%;
  filter: blur(60px);
  z-index: 0;
  animation: floatOrb 10s ease-in-out infinite;
}

.hero-container {
  width: 100%;
  max-width: 1300px;
  z-index: 2;
}

.glass-card {
  background: rgba(255,255,255,0.7);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255,255,255,0.8);
  border-radius: 40px;
  box-shadow: 0 40px 100px -20px rgba(50,50,93,0.15);
  display: flex;
  overflow: hidden;
  min-height: 400px;
}

.card-content {
  flex: 1;
  padding: 40px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
}

.text-wrapper {
  margin-bottom: 30px;
}

.title-container {
  margin-bottom: 20px;
}

.staggered-text {
  font-size: 3.5rem;
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 20px;
  letter-spacing: -1px;
  color: #3bc2ff;
}

.char {
  display: inline-block;
  animation: slideDownChar 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  opacity: 0;
}

.hero-subtitle {
  font-size: 1.25rem;
  color: #64748b;
  max-width: 500px;
  margin-bottom: 30px;
  opacity: 0;
  animation: blurIn 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards 0.4s;
}

.reveal-blur {
  opacity: 0;
  filter: blur(10px);
}

/* Visual Side */
.card-visual {
  flex: 1;
  position: relative;
  overflow: hidden;
}

.visual-bg {
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  transition: transform 0.8s cubic-bezier(0.4, 0, 0.2, 1), filter 0.8s ease;
  will-change: transform;
}

.glass-card:hover .visual-bg {
  transform: scale(1.08);
  filter: brightness(1.1) saturate(1.1);
}

.visual-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, #ffffff 0%, rgba(255,255,255,0) 100%);
}

.hero-nav {
  display: flex;
  gap: 15px;
  margin-top: 20px;
}

.nav-btn {
  position: relative;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 2px solid rgba(59, 194, 255, 0.2);
  background: rgba(255, 255, 255, 0.8);
  color: #3bc2ff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 1.2rem;
  overflow: hidden;
}

.nav-btn .btn-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(59, 194, 255, 0.6), transparent);
  transform: translate(-50%, -50%);
  transition: width 0.4s, height 0.4s;
}

.nav-btn:hover {
  border-color: #3bc2ff;
  background: #3bc2ff;
  color: white;
  transform: scale(1.15);
  box-shadow: 0 8px 25px rgba(59, 194, 255, 0.4);
}

.nav-btn:hover .btn-glow {
  width: 100px;
  height: 100px;
  animation: pulse 1.5s ease-in-out infinite;
}

/* Beam Search (The "Premium" Button style) */
.search-wrapper {
  max-width: 550px;
}

.search-label {
  font-size: 0.95rem;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 12px;
  display: block;
}

.filter-tabs { 
  margin-bottom: 15px; 
  display: flex; 
  gap: 10px; 
}

.tab-btn {
  background: transparent; 
  border: none; 
  padding: 8px 16px; 
  border-radius: 20px; 
  color: #94a3b8; 
  font-weight: 600; 
  cursor: pointer;
  transition: all 0.3s;
}

.tab-btn.active { 
  background: rgba(59,194,255,0.1); 
  color: #3bc2ff; 
}

.beam-container {
  position: relative;
  border-radius: 100px;
  padding: 2px; /* Border width */
  overflow: hidden;
  box-shadow: 0 10px 40px -10px rgba(59,194,255,0.3);
}

.beam-border {
  position: absolute; 
  top: -50%; 
  left: -50%; 
  width: 200%; 
  height: 200%;
  background: conic-gradient(transparent, transparent, #3bc2ff, transparent);
  animation: spin 3s linear infinite;
}

.search-inner {
  position: relative;
  background: white;
  border-radius: 100px;
  display: flex;
  align-items: center;
  padding: 8px 8px 8px 24px;
}

.search-icon {
  color: #94a3b8;
  font-size: 1.1rem;
  margin-right: 12px;
  transition: color 0.3s;
}

.search-inner:focus-within .search-icon {
  color: #3bc2ff;
}

.search-inner input {
  flex: 1; 
  border: none; 
  outline: none; 
  font-size: 1.1rem; 
  color: #334155;
}

.search-inner input::placeholder {
  color: #94a3b8;
}

.go-btn {
  position: relative;
  width: 48px; 
  height: 48px; 
  border-radius: 50%; 
  border: none;
  background: #f1f5f9; 
  color: #3bc2ff; 
  font-size: 1.2rem; 
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

.go-btn .btn-pulse {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(59, 194, 255, 0.4);
  transform: translate(-50%, -50%);
  animation: pulse 2s ease-in-out infinite;
}

.go-btn:hover { 
  transform: scale(1.15) rotate(90deg); 
  background: #3bc2ff; 
  color: white;
  box-shadow: 0 8px 25px rgba(59, 194, 255, 0.4);
}

.go-btn:active {
  transform: scale(1.1) rotate(90deg);
}

/* --- 2. CATEGORY MARQUEE (What are you looking for?) --- */
.category-marquee-section {
  padding: 60px 24px;
  background: #fdfdfd;
  position: relative;
  z-index: 1;
}

.category-header {
  max-width: 1200px;
  margin: 0 auto 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.category-title {
  font-size: 1.8rem;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
}

.see-more-btn {
  background: transparent;
  border: 2px solid #3bc2ff;
  color: #3bc2ff;
  padding: 10px 24px;
  border-radius: 50px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.see-more-btn::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(59, 194, 255, 0.1);
  transform: translate(-50%, -50%);
  transition: width 0.4s, height 0.4s;
}

.see-more-btn:hover::before {
  width: 200px;
  height: 200px;
}

.see-more-btn:hover {
  background: #3bc2ff;
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(59, 194, 255, 0.3);
}

.category-marquee-mask {
  mask-image: linear-gradient(to right, transparent, black 5%, black 95%, transparent);
  -webkit-mask-image: linear-gradient(to right, transparent, black 5%, black 95%, transparent);
  overflow: hidden;
}

.category-marquee-content {
  display: flex;
  width: max-content;
  animation: scroll 50s linear infinite;
}

.category-marquee-group {
  display: flex;
  gap: 40px;
  padding-right: 40px;
  align-items: center;
}

.category-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  min-width: 120px;
  padding: 20px 16px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 16px;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.05);
  transform: translateY(20px);
  opacity: 0;
  animation: slideUpFade 0.6s ease forwards;
}

.category-item i {
  font-size: 2rem;
  color: #3bc2ff;
  transition: all 0.3s ease;
}

.category-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: #334155;
  text-align: center;
  white-space: nowrap;
  transition: color 0.3s ease;
}

.category-item:hover {
  transform: translateY(-8px);
  box-shadow: 0 8px 24px rgba(59, 194, 255, 0.2);
  border-color: #3bc2ff;
  background: linear-gradient(135deg, rgba(59, 194, 255, 0.05) 0%, rgba(59, 194, 255, 0.02) 100%);
}

.category-item:hover i {
  color: #1da1f2;
  transform: scale(1.1);
}

.category-item:hover .category-label {
  color: #3bc2ff;
}

/* --- 3. MARQUEE (Trusted by students from) --- */
.marquee-section { 
  padding: 40px 0; 
  text-align: center; 
  overflow: hidden; 
}

.marquee-label { 
  text-transform: uppercase; 
  letter-spacing: 2px; 
  font-size: 0.8rem; 
  color: #94a3b8; 
  font-weight: 700; 
  margin-bottom: 20px; 
}

.marquee-mask {
  mask-image: linear-gradient(to right, transparent, black 10%, black 90%, transparent);
  -webkit-mask-image: linear-gradient(to right, transparent, black 10%, black 90%, transparent);
}

.marquee-content { 
  display: flex; 
  width: max-content; 
  animation: scroll 40s linear infinite; 
}

.marquee-group { 
  display: flex; 
  gap: 60px; 
  padding-right: 60px; 
}

.uni-logo { 
  font-size: 1.5rem; 
  font-weight: 800; 
  color: #cbd5e1; 
  white-space: nowrap;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: inline-block;
  transform: translateY(20px);
  opacity: 0;
  animation: slideUpFade 0.6s ease forwards;
}

.marquee-group:hover .uni-logo {
  color: #3bc2ff;
  transform: translateY(-2px);
}

/* --- 5. FEATURE GRID --- */
.content-container {
  max-width: 1200px;
  margin: 60px auto;
  padding: 0 24px;
  position: relative;
  z-index: 1;
  background: transparent;
}

.grid-layout {
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: 60px;
  align-items: start;
}

.left-sticky {
  opacity: 0;
  transform: translateY(30px);
}

.sticky-content {
  position: relative;
}

.sticky-content h2 {
  font-size: 2.8rem;
  margin-bottom: 1rem;
  line-height: 1.1;
  color: #0f172a;
  font-weight: 800;
}

.sticky-content p {
  color: #64748b;
  font-size: 1.1rem;
  line-height: 1.6;
}

.svg-decoration {
  margin-top: 40px;
  position: relative;
  overflow: hidden;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.1);
}

.hero-image {
  width: 100%;
  height: 300px;
  object-fit: cover;
  border-radius: 16px;
  display: block;
  transition: transform 0.3s ease;
}

.hero-image:hover {
  transform: scale(1.02);
}

.svg-decoration::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,0.3) 100%);
  border-radius: 16px;
  pointer-events: none;
  z-index: 1;
}

.hero-svg { 
  width: 120px; 
  height: auto; 
  opacity: 0.7; 
}

.feature-cards-container {
  background: white;
  border-radius: 24px;
  padding: 40px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.feature-cards-container::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #3bc2ff 0%, #1da1f2 100%);
  opacity: 0.8;
}

.right-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  position: relative;
}

.feature-connections {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0; /* Behind cards */
  opacity: 0;
  overflow: visible;
}

.right-grid.in-view .feature-connections {
  opacity: 1;
  transition: opacity 0.3s ease; /* Smooth fade-in */
}

.connection-line {
  stroke-dasharray: 1200; /* Matches ~path length for full draw */
  stroke-dashoffset: 1200;
  opacity: 0;
  animation: drawLine 2s ease-in-out forwards;
  fill: none;
  stroke-linecap: round;
  vector-effect: non-scaling-stroke; /* Scales with SVG */
}

.connection-line.line-1 {
  animation-delay: 0.5s;
}

.connection-line.line-2 {
  animation-delay: 1s;
}

.connection-line.line-3 {
  animation-delay: 1.5s;
  stroke-width: 1.5;
  opacity: 0.3; /* Inline opacity preserved */
  stroke-dasharray: 100; /* Shorter path, shorter dash */
  stroke-dashoffset: 100;
}

@keyframes drawLine {
  to {
    stroke-dashoffset: 0;
    opacity: 0.7; /* Final visible opacity */
  }
}

/* --- 6. ANIMATE ON SCROLL CLASSES --- */
.animate-on-scroll.in-view {
  animation: slideUpFade 0.8s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
}

.feature-card-wrapper {
  position: relative;
  opacity: 0;
  transform: translateY(30px);
}

.feature-card-wrapper.in-view {
  opacity: 1;
  animation: slideUpFade 0.8s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
}

.feature-card {
  background: white;
  padding: 3px;
  border-radius: 24px;
  box-shadow: 0 10px 40px rgba(0,0,0,0.04);
  border: 1px solid rgba(0,0,0,0.04);
  transition: all 0.4s cubic-bezier(0.2, 0.8, 0.2, 1);
  position: relative;
  z-index: 2;
  overflow: hidden;
}

/* Rotating Beam Border (like search bar) */
.card-beam-border {
  position: absolute;
  top: -50%; 
  left: -50%; 
  width: 200%; 
  height: 200%;
  background: conic-gradient(
    from 0deg, 
    transparent 0deg, 
    #3bc2ff 90deg, 
    transparent 180deg
  );
  animation: spin 4s linear infinite;
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
  z-index: 0;
}

/* Context-aware glow for each card color */
.card-blue .card-beam-border {
  background: conic-gradient(
    from 0deg, 
    transparent 0deg, 
    #0284c7 90deg, 
    transparent 180deg
  );
}

.card-purple .card-beam-border {
  background: conic-gradient(
    from 0deg, 
    transparent 0deg, 
    #9333ea 90deg, 
    transparent 180deg
  );
}

.card-green .card-beam-border {
  background: conic-gradient(
    from 0deg, 
    transparent 0deg, 
    #16a34a 90deg, 
    transparent 180deg
  );
}

.card-orange .card-beam-border {
  background: conic-gradient(
    from 0deg, 
    transparent 0deg, 
    #ea580c 90deg, 
    transparent 180deg
  );
}

/* Shine/Glow Effect */
.card-shine {
  position: absolute;
  top: 0;
  left: -100%;
  width: 50%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.4),
    transparent
  );
  transform: skewX(-20deg);
  transition: left 0.6s ease;
  pointer-events: none;
  z-index: 1;
}

/* Inner content wrapper */
.card-content-inner {
  position: relative;
  background: white;
  border-radius: 21px;
  padding: 36px;
  z-index: 2;
  transition: all 0.3s ease;
}

/* Hover Effects */
.feature-card-wrapper:hover .feature-card {
  transform: translateY(-8px);
  box-shadow: 0 20px 60px rgba(0,0,0,0.12);
}

.feature-card-wrapper:hover .card-beam-border {
  opacity: 1;
}

.feature-card-wrapper:hover .card-shine {
  left: 150%;
  transition: left 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Context-aware glow on hover */
.card-blue:hover .card-content-inner {
  box-shadow: 0 0 30px rgba(2, 132, 199, 0.15),
              inset 0 0 20px rgba(2, 132, 199, 0.05);
}

.card-purple:hover .card-content-inner {
  box-shadow: 0 0 30px rgba(147, 51, 234, 0.15),
              inset 0 0 20px rgba(147, 51, 234, 0.05);
}

.card-green:hover .card-content-inner {
  box-shadow: 0 0 30px rgba(22, 163, 74, 0.15),
              inset 0 0 20px rgba(22, 163, 74, 0.05);
}

.card-orange:hover .card-content-inner {
  box-shadow: 0 0 30px rgba(234, 88, 12, 0.15),
              inset 0 0 20px rgba(234, 88, 12, 0.05);
}

/* Subtle pulse animation when in view */
.feature-card-wrapper.in-view .card-beam-border {
  animation: spin 4s linear infinite, 
             cardGlow 3s ease-in-out infinite 1s;
}

@keyframes cardGlow {
  0%, 100% { opacity: 0; }
  50% { opacity: 0.6; }
}


.icon-box {
  width: 56px; 
  height: 56px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  font-size: 24px;
  background: #e7e7e7;
  box-shadow: inset 3px 3px 6px rgba(0,0,0,0.12),
              inset -3px -3px 6px rgba(255,255,255,0.8);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  transform-style: preserve-3d;
}

.icon-box::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(59, 194, 255, 0.3), transparent);
  transform: translate(-50%, -50%);
  transition: width 0.6s, height 0.6s;
}

.feature-card-wrapper:hover .icon-box {
  transform: translateY(-5px) rotateY(10deg) scale(1.1);
  box-shadow: 0 10px 30px rgba(0,0,0,0.15),
              inset 3px 3px 6px rgba(0,0,0,0.12),
              inset -3px -3px 6px rgba(255,255,255,0.8);
}

.feature-card-wrapper:hover .icon-box::before {
  width: 120px;
  height: 120px;
}

.blue { color: #0284c7; }
.purple { color: #9333ea; }
.green { color: #16a34a; }
.orange { color: #ea580c; }

.feature-card h3 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 10px;
  transition: all 0.3s ease;
  position: relative;
}

.feature-card-wrapper:hover .feature-card h3 {
  transform: translateX(5px);
  color: #3bc2ff;
}

.feature-card p {
  font-size: 0.95rem;
  color: #64748b;
  line-height: 1.6;
}

/* Removed - now using feature-card-wrapper for animation */

/* --- 6. REVIEWS SECTION --- */
.reviews-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}

.section-title {
  font-size: 1.8rem;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
}

.reviews-nav {
  display: flex;
  gap: 8px;
}

.nav-arrow {
  position: relative;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #e7e7e7;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: inset 2px 2px 5px rgba(0,0,0,0.1),
              inset -2px -2px 5px rgba(255,255,255,0.9);
  overflow: hidden;
}

.nav-arrow::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(59, 194, 255, 0.3), transparent);
  transform: translate(-50%, -50%);
  transition: width 0.4s, height 0.4s;
}

.nav-arrow:hover:not(:disabled) {
  color: #3bc2ff;
  transform: scale(1.1) rotate(5deg);
  box-shadow: 0 8px 20px rgba(59, 194, 255, 0.2);
}

.nav-arrow:hover:not(:disabled)::before {
  width: 100px;
  height: 100px;
}

.nav-arrow:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.reviews-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 60px;
}

.review-card {
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.03);
  border: 1px solid rgba(0,0,0,0.05);
  display: flex;
  flex-direction: column;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  height: 200px;
  overflow: hidden;
  position: relative;
  transform-style: preserve-3d;
}

.review-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(59, 194, 255, 0.1), transparent);
  transition: left 0.6s ease;
  z-index: 1;
}

.review-card:hover::before {
  left: 100%;
}

.review-card:hover {
  transform: translateY(-8px) rotateX(2deg);
  box-shadow: 0 15px 40px rgba(59,194,255,0.2);
  border-color: #3bc2ff;
}

.review-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  flex-shrink: 0;
}

.reviewer-avatar-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 14px;
  flex-shrink: 0;
}

.reviewer-avatar-wrapper {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.reviewer-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.reviewer-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
}

.reviewer-info {
  display: flex;
  flex-direction: column;
}

.reviewer-name {
  font-weight: 700;
  font-size: 0.95rem;
  color: #0f172a;
}

.stars {
  display: flex;
  gap: 2px;
}

.stars .el-icon-star-on {
  color: #cbd5e1; /* inactive */
  font-size: 14px;
}

.stars .el-icon-star-on.active.star-green {
  color: #00b67a; /* Green for 4-5 stars */
}

.stars .el-icon-star-on.active.star-red {
  color: #ef4444; /* Red for 1 star */
}

.stars .el-icon-star-on.active:not(.star-green):not(.star-red) {
  color: #fbbf24; /* Yellow/Orange for 2-3 stars */
}

.review-text {
  font-size: 0.95rem;
  color: #334155;
  line-height: 1.5;
  margin-bottom: 16px;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
}

.review-footer {
  margin-top: auto;
  padding-top: 12px;
  border-top: 1px solid #f1f5f9;
  flex-shrink: 0;
}

.review-company {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  color: #64748b;
  font-weight: 500;
}

.company-logo {
  width: 20px;
  height: 20px;
  object-fit: contain;
  border-radius: 4px;
}

.reviews-empty {
  text-align: center;
  padding: 60px 20px;
  color: #64748b;
  font-size: 1rem;
}

.reviews-empty p {
  margin: 8px 0;
}

.reviews-empty-subtitle {
  font-size: 0.9rem;
  color: #94a3b8;
  margin-top: 8px;
}

/* --- 8. POPULAR LISTINGS SECTION --- */
.popular-listings-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  gap: 20px;
}

.popular-listings-title-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
}

.popular-listings-subtitle {
  font-size: 1rem;
  color: #64748b;
  font-weight: 400;
  margin: 0;
}

.popular-see-more-btn {
  background: transparent;
  border: 2px solid #3bc2ff;
  color: #3bc2ff;
  padding: 10px 24px;
  border-radius: 50px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
  white-space: nowrap;
}

.popular-see-more-btn::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(59, 194, 255, 0.1);
  transform: translate(-50%, -50%);
  transition: width 0.4s, height 0.4s;
}

.popular-see-more-btn:hover::before {
  width: 200px;
  height: 200px;
}

.popular-see-more-btn:hover {
  background: #3bc2ff;
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(59, 194, 255, 0.3);
}

.popular-listings-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  margin-bottom: 60px;
}

@media (max-width: 1200px) {
  .popular-listings-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

.popular-listings-empty {
  grid-column: 1 / -1;
  text-align: center;
  padding: 60px 20px;
  color: #64748b;
  font-size: 1.1rem;
}

.popular-card {
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.03);
  border: 1px solid rgba(0,0,0,0.05);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  position: relative;
  overflow: hidden;
}

.popular-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(59, 194, 255, 0.1), transparent);
  transition: left 0.6s ease;
  z-index: 1;
}

.popular-card:hover::before {
  left: 100%;
}

.popular-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 15px 40px rgba(59,194,255,0.2);
  border-color: #3bc2ff;
}

.pop-card-icon {
  width: 60px;
  height: 60px;
  border-radius: 12px;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  overflow: hidden;
  flex-shrink: 0;
}

.pop-card-icon img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 12px;
}

.pop-card-content {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.pop-card-content h3 {
  font-size: 1.1rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.pop-card-content p {
  font-size: 0.9rem;
  color: #64748b;
  margin: 0;
}

.pop-rating {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
}

.pop-rating .stars {
  display: flex;
  gap: 2px;
}

.pop-rating .stars .el-icon-star-on {
  color: #cbd5e1;
  font-size: 14px;
}

.pop-rating .stars .el-icon-star-on.active.star-green {
  color: #00b67a;
}

.pop-rating .rating-text {
  font-size: 0.85rem;
  color: #64748b;
  font-weight: 500;
}

/* --- 7. LEASING & STARBUCKS CARDS --- */
.full-width-section {
  max-width: 1600px;
  margin: 0 auto;
  padding: 0 24px;
  opacity: 0;
  transform: translateY(30px);
  overflow: visible;
}

.property-manager-section {
  margin-top: 0;
  padding-top: 0;
  margin-bottom: 0;
}

.starbucks-section {
  margin-top: 32px;
  padding-top: 0;
  overflow: visible;
  width: 100%;
  max-width: 1600px;
  margin-left: auto;
  margin-right: auto;
  padding-left: 24px;
  padding-right: 24px;
  box-sizing: border-box;
}

.starbucks-carousel-wrapper {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  overflow: visible;
}

.starbucks-hero-card {
  position: relative;
  background: linear-gradient(135deg, #006241 0%, #00704a 52%, #004d33 100%);
  color: #fff;
  border-radius: 28px;
  padding: 28px 32px 26px;
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 28px;
  box-shadow: 0 22px 56px rgba(0, 72, 51, 0.38);
  box-sizing: border-box;
  width: 100%;
}

.sb-hero-main {
  flex: 1;
  min-width: 0;
}

.sb-hero-title {
  margin: 0 0 10px;
  font-size: clamp(1.35rem, 3.5vw, 1.85rem);
  font-weight: 800;
  line-height: 1.2;
  letter-spacing: -0.02em;
}

.sb-hero-highlight {
  color: #d4e9e2;
  white-space: nowrap;
}

.sb-hero-lead {
  margin: 0 0 18px;
  font-size: 0.95rem;
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.92);
}

.sb-hero-lead strong {
  color: #fff;
  font-weight: 700;
}

.sb-progress-block {
  margin-bottom: 16px;
}

.sb-progress-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}

.sb-progress-label {
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.82);
}

.sb-progress-pill {
  flex-shrink: 0;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.35);
}

.sb-progress-track {
  height: 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.22);
  overflow: hidden;
}

.sb-progress-fill {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #fff 0%, #c8e6d9 100%);
  transition: width 0.45s ease;
}

.sb-btn-viewmore {
  margin-top: 2px;
}

.sb-btn-secondary {
  margin-top: 12px;
  background: rgba(255, 255, 255, 0.95);
  color: #006241;
}

.sb-details {
  margin-top: 18px;
  padding-top: 18px;
  border-top: 1px solid rgba(255, 255, 255, 0.22);
}

.sb-details-intro {
  margin: 0 0 10px;
  font-size: 0.9rem;
  font-weight: 700;
}

.sb-details-list {
  margin: 0 0 12px;
  padding-left: 1.2rem;
  font-size: 0.88rem;
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.9);
}

.sb-details-list li + li {
  margin-top: 6px;
}

.sb-details-foot {
  margin: 0 0 8px;
  font-size: 0.82rem;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.78);
}

.sb-fade-enter-active,
.sb-fade-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}

.sb-fade-enter,
.sb-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.sb-hero-logo-wrap {
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.sb-hero-logo-ring {
  width: 112px;
  height: 112px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.14);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.2);
}

.sb-hero-logo-ring img {
  width: 64px;
  height: 64px;
  object-fit: contain;
}

@media (max-width: 720px) {
  .starbucks-hero-card {
    flex-direction: column;
    align-items: stretch;
    padding: 22px 20px;
  }

  .sb-hero-logo-wrap {
    justify-content: flex-end;
    margin-top: -8px;
  }

  .sb-hero-logo-ring {
    width: 88px;
    height: 88px;
  }

  .sb-hero-logo-ring img {
    width: 52px;
    height: 52px;
  }
}

.full-width-section.in-view {
  opacity: 1;
  transform: translateY(0);
}

.leasing-glass-card {
  background: linear-gradient(135deg, #1c1c1c 0%, #0f0f0f 100%);
  color: white;
  border-radius: 32px;
  padding: 32px 50px;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.4s ease;
  transform-style: preserve-3d;
  opacity: 0;
  transform: translateY(30px);
  width: 100%;
  max-width: 1600px;
  margin: 0 auto;
}

.leasing-glass-card.in-view {
  opacity: 1;
  transform: translateY(0);
  animation: slideUpFade 0.8s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
}

.leasing-bg-bars {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: space-around;
  gap: 16px;
  padding: 0 30px 30px;
  opacity: 0.12;
  pointer-events: none;
  z-index: 0;
}

.leasing-bg-bars::before,
.leasing-bg-bars::after {
  content: '';
  position: absolute;
  bottom: 0;
  width: 12px;
  background: rgba(255, 255, 255, 0.4);
  border-radius: 6px 6px 0 0;
  animation: barGrow 1.2s ease-out backwards;
}

.leasing-bg-bars::before {
  left: 8%;
  height: 35%;
  animation-delay: 0.2s;
}

.leasing-bg-bars::after {
  right: 12%;
  height: 55%;
  animation-delay: 0.4s;
}

.leasing-glass-card::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 20%;
  width: 10px;
  height: 45%;
  background: rgba(255, 255, 255, 0.35);
  border-radius: 5px 5px 0 0;
  opacity: 0.12;
  animation: barGrow 1.2s ease-out 0.3s backwards;
  pointer-events: none;
  z-index: 0;
}

.leasing-glass-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 25px 70px rgba(0, 0, 0, 0.4);
}

.leasing-content {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 40px;
}

.leasing-text-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
}

.leasing-subtitle {
  font-size: 0.95rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
  letter-spacing: 0.5px;
}

.leasing-content h2 {
  font-size: 1.8rem;
  font-weight: 800;
  margin: 0;
  line-height: 1.3;
  color: white;
}

.leasing-content p {
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 24px;
}

.leasing-get-started-btn {
  background: white;
  color: #1c1c1c;
  border: none;
  padding: 14px 32px;
  border-radius: 50px;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
  white-space: nowrap;
}

.leasing-get-started-btn::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(28, 28, 28, 0.1);
  transform: translate(-50%, -50%);
  transition: width 0.6s, height 0.6s;
}

.leasing-get-started-btn:hover::before {
  width: 300px;
  height: 300px;
}

.leasing-get-started-btn:hover {
  transform: translateY(-4px) scale(1.05);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.25);
}

.leasing-get-started-btn:active {
  transform: translateY(-2px) scale(1.02);
}

@keyframes barGrow {
  from {
    height: 0;
    opacity: 0;
  }
  to {
    opacity: 0.3;
  }
}

/* Enhanced Premium Button with Micro-interactions */
.premium-btn {
  position: relative;
  background: white;
  color: #0f172a;
  border: none;
  padding: 16px 36px;
  border-radius: 12px;
  font-weight: 700;
  font-size: 1rem;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform-style: preserve-3d;
}

.premium-btn .btn-content {
  position: relative;
  z-index: 2;
}

.premium-btn .btn-ripple-effect {
  position: absolute;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(59, 194, 255, 0.3);
  transform: translate(-50%, -50%);
  pointer-events: none;
  transition: width 0.6s, height 0.6s;
}

.premium-btn:hover {
  transform: translateY(-4px) scale(1.02);
  box-shadow: 0 12px 40px rgba(59, 194, 255, 0.3);
}

.premium-btn:active {
  transform: translateY(-2px) scale(0.98);
}

.magnetic-btn {
  transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-shine {
  position: absolute;
  top: 0; left: -100%;
  width: 50%; height: 100%;
  background: linear-gradient(to right, transparent, rgba(255,255,255,0.5), transparent);
  transform: skewX(-20deg);
  animation: shine 3s infinite;
}

.starbucks-carousel-container {
  position: relative;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  min-height: 120px;
  perspective: 1200px;
  perspective-origin: center center;
  overflow: visible;
  padding: 0;
}

.starbucks-carousel-container::before,
.starbucks-carousel-container::after {
  content: '';
  display: none;
}

.starbucks-card {
  background: linear-gradient(135deg, #006241 0%, #00704a 100%);
  color: white;
  border-radius: 32px;
  padding: 32px 50px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  max-width: 1200px;
  transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  transform-style: preserve-3d;
  overflow: visible !important;
  cursor: pointer;
  box-sizing: border-box;
  white-space: normal;
}

.sb-card-front {
  z-index: 3;
  opacity: 1;
  transform: translate(-50%, -50%) translateY(0) scale(1) rotateY(0deg);
  box-shadow: 0 20px 60px rgba(0, 98, 65, 0.4);
  pointer-events: auto;
  overflow: visible !important;
}

.sb-card-front * {
  opacity: 1 !important;
  filter: none !important;
  visibility: visible !important;
  text-shadow: none !important;
}

.sb-card-front .sb-text,
.sb-card-front .sb-icon,
.sb-card-front .sb-btn,
.sb-card-front .sb-text *,
.sb-card-front .sb-text h2,
.sb-card-front .sb-text p {
  opacity: 1 !important;
  filter: none !important;
  visibility: visible !important;
  text-shadow: none !important;
}

.sb-card-back-left {
  z-index: 1;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
}

.sb-card-back-right {
  z-index: 2;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
}

.sb-card-front:hover {
  transform: translate(-50%, -50%) translateY(-8px) scale(1.02) rotateY(0deg);
  box-shadow: 0 25px 70px rgba(0, 98, 65, 0.5);
}

.starbucks-card .sb-icon {
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.sb-card-front:hover .sb-icon {
  transform: scale(1.15) rotate(10deg);
}

.sb-text h2 {
  font-size: 1.6rem;
  font-weight: 800;
  margin-bottom: 8px;
  line-height: 1.1;
  color: white;
}

.sb-text p {
  font-size: 0.95rem;
  opacity: 0.9;
  margin-bottom: 16px;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.5;
}

.sb-icon {
  width: 100px;
  height: 100px;
  background: rgba(255,255,255,0.15);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sb-icon img {
  width: 60px;
  height: 60px;
  object-fit: contain;
}

.sb-btn {
  position: relative;
  background: white;
  color: #006241;
  border: none;
  padding: 14px 32px;
  border-radius: 50px;
  font-weight: 700;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

.sb-btn::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(0, 98, 65, 0.1);
  transform: translate(-50%, -50%);
  transition: width 0.6s, height 0.6s;
}

.sb-btn:hover::before {
  width: 300px;
  height: 300px;
}

.sb-btn:hover {
  transform: translateY(-4px) scale(1.05);
  box-shadow: 0 12px 30px rgba(0,0,0,0.25);
}

.sb-btn:active {
  transform: translateY(-2px) scale(1.02);
}

/* --- 8. MODAL & FORM --- */
.modal-backdrop {
  position: fixed; 
  top: 0; left: 0; 
  width: 100%; height: 100%;
  background: rgba(0,0,0,0.6);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex; 
  justify-content: center; 
  align-items: center;
  z-index: 9999;
  padding: 20px;
}

.modal-glass {
  background: white;
  padding: 48px;
  border-radius: 28px;
  width: 100%;
  max-width: 480px;
  box-sizing: border-box;
  max-height: min(88vh, 760px);
  overflow-y: auto;
  box-shadow: 0 25px 80px rgba(0,0,0,0.25);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.modal-header h3 {
  font-size: 1.8rem;
  font-weight: 800;
  color: #0f172a;
}

.close-btn {
  background: none;
  border: none;
  font-size: 2rem;
  color: #94a3b8;
  cursor: pointer;
  transition: color 0.2s;
}

.close-btn:hover {
  color: #0f172a;
}

.modal-subtitle {
  color: #64748b;
  margin-bottom: 32px;
  font-size: 1rem;
  line-height: 1.5;
}

.modern-form {
  padding: 0 32px 32px 32px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  background: transparent;
}

.input-group { 
  position: relative; 
  margin-bottom: 28px; 
}

.input-group input {
  width: 100%;
  padding: 16px 0;
  border: none;
  border-bottom: 2px solid #e2e8f0;
  outline: none;
  font-size: 1rem;
  background: transparent;
  transition: border-color 0.3s;
}

.input-group input:focus {
  border-color: #3bc2ff;
}

.input-group label {
  position: absolute; 
  top: 16px; 
  left: 0;
  color: #94a3b8;
  transition: 0.3s;
  pointer-events: none;
  font-size: 1rem;
}

.input-group input:focus ~ label,
.input-group input:not(:placeholder-shown) ~ label {
  top: -12px; 
  font-size: 0.8rem; 
  color: #3bc2ff;
  font-weight: 600;
}


.checkbox-group {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 0;
  padding: 8px 0;
}

.checkbox-group input[type="checkbox"] {
  width: 20px;
  height: 20px;
  cursor: pointer;
  accent-color: #3bc2ff;
  margin-top: 2px;
  flex-shrink: 0;
}

.checkbox-group label {
  color: #718096;
  font-size: 14px;
  cursor: pointer;
  line-height: 1.5;
  user-select: none;
  position: static;
  background: none;
  padding: 0;
}

.checkbox-group label:hover {
  color: #3bc2ff;
}

.submit-btn-full {
  width: 100%;
  padding: 18px;
  background: linear-gradient(135deg, #3bc2ff 0%, #1da1f2 100%);
  color: white;
  border: none;
  border-radius: 14px;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 20px rgba(59, 194, 255, 0.3);
}

.submit-btn-full:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(59, 194, 255, 0.4);
}

/* --- 9. FOOTER --- */
.modern-footer {
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  padding: 80px 24px 50px;
  margin-top: 80px;
  position: static;
}

.footer-grid {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 2fr;
  gap: 48px;
}

.footer-brand h3 {
  font-size: 1.5rem;
  font-weight: 800;
  color: #0f172a;
  margin-bottom: 12px;
}

.footer-brand p {
  color: #64748b;
  margin-bottom: 20px;
  line-height: 1.6;
}

.social-icons {
  display: flex;
  gap: 16px;
}

.social-icons a {
  width: 40px;
  height: 40px;
  background: #e7e7e7;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3bc2ff;
  text-decoration: none;
  transition: all 0.2s;
  box-shadow: inset 2px 2px 5px rgba(0,0,0,0.1),
              inset -2px -2px 5px rgba(255,255,255,0.9);
}

.social-icons a:hover {
  transform: translateY(-3px);
  color: #1da1f2;
}

.footer-links h4 {
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 20px;
}

.footer-links a {
  display: block;
  color: #64748b;
  text-decoration: none;
  margin-bottom: 12px;
  font-size: 0.95rem;
  transition: color 0.2s;
}

.footer-links a:hover { 
  color: #3bc2ff; 
}

.footer-subscribe h4 {
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 12px;
}

.footer-subscribe p {
  color: #64748b;
  font-size: 0.9rem;
  margin-bottom: 16px;
}

.sub-input {
  display: flex;
  background: white;
  border-radius: 14px;
  padding: 6px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.06);
}

.sub-input input {
  border: none; 
  outline: none; 
  padding: 12px 16px; 
  flex: 1;
  font-size: 0.95rem;
  border-radius: 10px;
}

.sub-input button {
  background: #3bc2ff; 
  color: white;
  border: none; 
  border-radius: 10px; 
  width: 48px;
  height: 48px;
  cursor: pointer;
  transition: background 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}

.sub-input button:hover {
  background: #1da1f2;
}

/* --- TRANSITIONS --- */
.card-fade-enter-active, 
.card-fade-leave-active { 
  transition: opacity 0.5s, transform 0.5s; 
}

.card-fade-enter { 
  opacity: 0; 
  transform: scale(0.95); 
}

.card-fade-leave-to { 
  opacity: 0; 
  transform: scale(1.05); 
}

/* Text fade transition - for title and subtitle only */
.text-fade-enter-active,
.text-fade-leave-active {
  transition: opacity 0.5s cubic-bezier(0.34, 1.56, 0.64, 1), 
              transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.text-fade-enter {
  opacity: 0;
  transform: translateY(20px) scale(0.95);
}

.text-fade-leave-to {
  opacity: 0;
  transform: translateY(-15px) scale(1.02);
}

/* Background image fade transition */
.bg-fade-enter-active {
  transition: opacity 0.7s cubic-bezier(0.4, 0, 0.2, 1),
              transform 0.7s cubic-bezier(0.4, 0, 0.2, 1);
}

.bg-fade-leave-active {
  transition: opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1),
              transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.bg-fade-enter {
  opacity: 0;
  transform: scale(1.08);
}

.bg-fade-leave-to {
  opacity: 0;
  transform: scale(0.98);
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}

/* --- 8. TRUSTPILOT HERO BANNER --- */
.tp-hero-banner {
  background-color: #f1e5d1; /* Warm Beige/Peach */
  border-radius: 32px;
  padding: 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 40px;
  position: relative;
  overflow: hidden;
  margin-bottom: 40px;
}

.tp-hero-content {
  flex: 1;
  max-width: 600px;
  z-index: 2;
}

.tp-hero-content h1 {
  font-size: 2.5rem;
  font-weight: 800;
  color: #1c1c1c;
  line-height: 1.1;
  margin-bottom: 12px;
}

.tp-hero-content p {
  font-size: 1.1rem;
  color: #4b4b4b;
  margin-bottom: 24px;
  line-height: 1.5;
}

.tp-hero-actions {
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

.tp-black-btn {
  background-color: #1c1c1c;
  color: white;
  border: none;
  padding: 14px 32px;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: transform 0.2s;
}

.tp-black-btn:hover {
  transform: translateY(-2px);
  background-color: #000;
}


.tp-hero-images {
  flex: 1;
  display: flex;
  justify-content: center;
  height: 280px;
  position: relative;
}

.hero-image-group {
  display: flex;
  gap: 16px;
  height: 100%;
}

.hero-img-card-wrapper {
  width: 140px;
  height: 100%;
  position: relative;
}

.hero-img-card {
  width: 140px;
  height: 100%;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  position: absolute;
  top: 0;
  left: 0;
}

.hero-img-card img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Stagger visual effect for 3 cards */
.hero-img-card:nth-child(2) {
  transform: translateY(-20px); /* Middle card higher */
}

.hero-img-card:nth-child(odd) {
  transform: translateY(20px);
}

.banner-fade-enter-active {
  transition: opacity 0.6s ease var(--delay, 0s), transform 0.6s ease var(--delay, 0s);
}
.banner-fade-leave-active {
  transition: opacity 0.4s ease, transform 0.4s ease;
}
.banner-fade-enter {
  opacity: 0;
  transform: translateY(30px) scale(0.9);
}
.banner-fade-leave-to {
  opacity: 0;
  transform: translateY(-20px) scale(1.05);
}

.staggered-image {
  opacity: 0;
  transform: translateY(40px) scale(0.9);
  animation: imageStaggerIn 1s ease forwards;
}

@keyframes imageStaggerIn {
  0% {
    opacity: 0;
    transform: translateY(40px) scale(0.9);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* --- TABLET RESPONSIVE --- */
@media (max-width: 768px) {
  .hero-section { 
    min-height: auto; 
    padding: 24px 16px 32px; 
  }

  .tp-hero-banner {
    flex-direction: column;
    padding: 36px 28px;
    gap: 32px;
  }

  .tp-hero-content {
    max-width: 100%;
    text-align: center;
  }

  .tp-hero-content h1 {
    font-size: 2rem;
  }

  .tp-hero-content p {
    font-size: 1.05rem;
  }

  .tp-hero-actions {
    justify-content: center;
  }

  .tp-hero-images {
    height: 240px;
  }

  .hero-img-card-wrapper {
    width: 120px;
  }

  .hero-img-card {
    width: 120px;
  }

  .category-marquee-section {
    padding: 40px 20px;
  }

  .category-header {
    margin-bottom: 30px;
  }

  .category-title {
    font-size: 1.6rem;
  }

  .category-item {
    min-width: 110px;
    padding: 18px 14px;
  }

  .category-item i {
    font-size: 1.75rem;
  }

  .category-marquee-group {
    gap: 32px;
    padding-right: 32px;
  }
  
  .glass-card { 
    flex-direction: column-reverse; 
    margin: 0;
    min-height: auto;
    border-radius: 24px;
  }
  
  .card-content { 
    max-width: 100%; 
    padding: 32px 24px; 
  }
  
  .staggered-text {
    font-size: 2rem;
  }
  
  .hero-subtitle {
    font-size: 1rem;
    margin-bottom: 24px;
  }
  
  .filter-tabs {
    flex-wrap: wrap;
    justify-content: center;
  }
  
  .tab-btn {
    padding: 6px 12px;
    font-size: 0.85rem;
  }
  
  .grid-layout { 
    grid-template-columns: 1fr; 
    gap: 40px;
  }
  
  .sticky-content {
    position: relative;
    top: 0;
    text-align: center;
  }

  .sticky-content h2 {
    font-size: 2rem;
  }
  
  .svg-decoration {
    display: none;
  }
  
  .feature-cards-container {
    padding: 30px 24px;
    border-radius: 20px;
  }
  
  .right-grid {
    grid-template-columns: 1fr;
  }
  
  .feature-card {
    padding: 28px;
  }
  
  .reviews-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }

  .popular-listings-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }

  .popular-listings-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .leasing-glass-card {
    padding: 28px 32px;
    flex-direction: column;
    align-items: flex-start;
    gap: 24px;
  }

  .leasing-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 24px;
    width: 100%;
  }

  .leasing-text-group {
    width: 100%;
  }

  .leasing-content h2 {
    font-size: 1.5rem;
  }

  .leasing-subtitle {
    font-size: 0.9rem;
  }

  .leasing-get-started-btn {
    padding: 12px 28px;
    font-size: 0.95rem;
    width: 100%;
    text-align: center;
  }
  
  .starbucks-carousel-container {
    height: auto;
    min-height: 280px;
    padding: 0;
  }

  .starbucks-card {
    padding: 36px 28px;
    flex-direction: column;
    text-align: center;
    gap: 24px;
  }

  .sb-card-back-left,
  .sb-card-back-right {
    opacity: 0;
    visibility: hidden;
  }
  
  .sb-text h2 {
    font-size: 1.6rem;
  }
  
  .sb-icon {
    width: 100px;
    height: 100px;
  }
  
  .sb-icon img {
    width: 60px;
    height: 60px;
  }
  
  .footer-grid { 
    grid-template-columns: 1fr; 
    gap: 36px;
    text-align: center;
  }
  
  .social-icons {
    justify-content: center;
  }
  
  .modal-glass {
    max-width: calc(100% - 20px);
    width: calc(100% - 20px);
    margin: 10px;
    border-radius: 18px;
    padding: 24px 20px;
    max-height: calc(100vh - 20px);
  }

  .modal-header {
    padding: 0;
    margin-bottom: 12px;
  }

  .modal-header h3 {
    font-size: 1.5rem;
  }

  .modal-subtitle {
    padding: 0;
    margin-bottom: 16px;
    font-size: 0.9rem;
  }

  .modern-form {
    padding: 0;
    gap: 20px;
  }

  .input-group input {
    padding: 10px 12px;
    font-size: 0.95rem;
  }

  .close-btn {
    width: 32px;
    height: 32px;
    font-size: 1.3rem;
  }

  .submit-btn-full {
    padding: 14px 20px;
    font-size: 0.95rem;
  }
}

/* --- MOBILE RESPONSIVE --- */
@media (max-width: 480px) {
  .modal-backdrop {
    padding: 8px;
    align-items: flex-start;
  }

  .modal-glass {
    width: 100%;
    max-width: 100%;
    margin: 0;
    border-radius: 14px;
    padding: 18px 14px;
    max-height: calc(100vh - 16px);
  }

  .modal-header h3 {
    font-size: 1.2rem;
    line-height: 1.3;
  }

  .modal-subtitle {
    font-size: 0.85rem;
    margin-bottom: 12px;
  }

  .staggered-text {
    font-size: 1.6rem;
  }
  
  .card-content {
    padding: 24px 20px;
  }
  
  .search-inner {
    padding: 6px 6px 6px 16px;
  }
  
  .search-inner input {
    font-size: 0.9rem;
  }
  
  .go-btn {
    width: 40px;
    height: 40px;
  }
  
  .content-container {
    margin: 50px auto;
    padding: 0 16px;
  }
  
  .full-width-section {
    padding: 0 16px;
    margin: 40px 0;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }
  
  .category-marquee-section {
    padding: 40px 16px;
  }

  .category-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
    margin-bottom: 30px;
  }

  .category-title {
    font-size: 1.5rem;
  }

  .see-more-btn {
    padding: 8px 20px;
    font-size: 0.85rem;
  }

  .category-item {
    min-width: 100px;
    padding: 16px 12px;
    gap: 8px;
  }

  .category-item i {
    font-size: 1.5rem;
  }

  .category-label {
    font-size: 0.8rem;
  }

  .category-marquee-group {
    gap: 24px;
    padding-right: 24px;
  }

  .marquee-group span {
    font-size: 1.1rem;
  }
  
  .reviews-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  
  .reviews-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
  
  .section-title {
    font-size: 1.5rem;
  }
  
  .review-card {
    padding: 16px;
    height: auto;
    min-height: 100px;
    flex-direction: row;
    align-items: flex-start;
    gap: 12px;
  }

  .review-header {
    flex-direction: column;
    align-items: center;
    gap: 6px;
    margin-bottom: 0;
    flex-shrink: 0;
    width: auto;
  }

  .reviewer-avatar-circle {
    width: 45px;
    height: 45px;
    font-size: 14px;
  }

  .reviewer-info {
    text-align: center;
    width: 100%;
    min-width: 60px;
  }

  .reviewer-name {
    font-size: 0.75rem;
    margin-bottom: 2px;
    line-height: 1.2;
  }

  .stars {
    justify-content: center;
  }

  .stars i {
    font-size: 10px;
  }

  .review-text {
    flex: 1;
    font-size: 0.85rem;
    line-height: 1.4;
    margin: 0;
    display: -webkit-box;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;
    overflow: hidden;
    min-width: 0;
  }

  .review-footer {
    padding-top: 8px;
    font-size: 0.8rem;
  }

  .popular-listings-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .popular-card {
    flex-direction: row;
    align-items: center;
    text-align: left;
    padding: 16px;
    gap: 16px;
  }

  .pop-card-icon {
    flex-shrink: 0;
    width: 80px;
    height: 80px;
    margin-bottom: 0;
  }

  .pop-card-icon img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 12px;
  }

  .pop-card-content {
    flex: 1;
    min-width: 0;
    align-items: flex-start;
  }

  .pop-card-content h3 {
    font-size: 1rem;
    margin-bottom: 6px;
  }

  .pop-card-content p {
    font-size: 0.85rem;
    margin-bottom: 8px;
  }

  .pop-rating {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .pop-rating .stars {
    justify-content: flex-start;
  }

  .pop-rating .stars i {
    font-size: 12px;
  }

  .rating-text {
    font-size: 0.8rem;
  }

  .feature-cards-container {
    padding: 20px 16px;
    border-radius: 20px;
  }
  
  .right-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  
  .feature-card {
    padding: 3px;
  }

  .card-content-inner {
    padding: 20px;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 16px;
  }

  .icon-box {
    width: 60px;
    height: 60px;
    flex-shrink: 0;
    margin-bottom: 0;
  }

  .icon-box i {
    font-size: 24px;
  }

  .feature-card h3 {
    font-size: 1rem;
    margin-bottom: 6px;
  }

  .feature-card p {
    font-size: 0.85rem;
    margin: 0;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .popular-listings-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .popular-see-more-btn {
    padding: 8px 20px;
    font-size: 0.85rem;
  }

  .starbucks-carousel-container {
    height: auto;
    min-height: 240px;
  }

  .starbucks-card {
    padding: 36px 28px;
    min-height: 200px;
  }

  .sb-card-back-left,
  .sb-card-back-right {
    display: none;
  }

  /* Hero Banner Mobile Styles */
  .tp-hero-banner {
    flex-direction: column;
    padding: 24px 16px;
    gap: 20px;
    border-radius: 24px;
    margin-left: 0;
    margin-right: 0;
    width: 100%;
    box-sizing: border-box;
  }

  .tp-hero-content {
    max-width: 100%;
    text-align: center;
    width: 100%;
  }

  .tp-hero-content h1 {
    font-size: 1.5rem;
    margin-bottom: 12px;
    line-height: 1.2;
    padding: 0 4px;
  }

  .tp-hero-content p {
    font-size: 0.95rem;
    margin-bottom: 20px;
    line-height: 1.5;
    padding: 0 4px;
  }

  .tp-hero-actions {
    justify-content: center;
    width: 100%;
  }

  .tp-black-btn {
    padding: 14px 24px;
    font-size: 0.95rem;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }

  .tp-hero-images {
    height: 200px;
    width: 100%;
  }

  .hero-img-card-wrapper {
    width: 100px;
  }

  .hero-img-card {
    width: 100px;
  }

  .tp-hero-images {
    height: 200px;
    width: 100%;
  }

  .hero-image-group {
    gap: 12px;
    justify-content: center;
  }

  .hero-img-card-wrapper {
    width: 100px;
  }

  .hero-img-card {
    width: 100px;
  }
}
</style>
